import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  LogOut, FolderOpen, Image as ImageIcon, CreditCard, Trash2, Download, 
  ExternalLink, Plus, Crown, Check, Eye, LayoutDashboard, Video, Film, 
  Clapperboard, BookImage, Wallet, Play, ChevronRight, TrendingUp, 
  Clock, CheckCircle2, Menu, X, Sparkles, Users, Shirt, Tag, Package,
  Upload, ChevronDown, ChevronUp, Wand2, Calendar, Building2, Mic, Pencil
} from "lucide-react";
import { toast } from "sonner";
import { SeriesEpisodesList } from "@/components/SeriesEpisodesList";
import BrandsAndProducts from "@/components/BrandsAndProducts";
import { VoiceSampleControls, VoiceLibraryModal } from "@/components/VoiceControls";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// Sidebar Navigation Item Component
const SidebarItem = ({ icon: Icon, label, isActive, onClick, badge, locked }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
      isActive 
        ? "bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-600 border-l-4 border-blue-500" 
        : "text-gray-600 hover:bg-gray-100"
    } ${locked ? "opacity-60" : ""}`}
  >
    <Icon className={`w-5 h-5 ${isActive ? "text-blue-600" : "text-gray-500"}`} />
    <span className={`font-medium ${isActive ? "text-blue-600" : ""}`}>{label}</span>
    {badge && (
      <span className="ml-auto px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700">
        {badge}
      </span>
    )}
    {locked && (
      <Crown className="ml-auto w-4 h-4 text-yellow-500" />
    )}
  </button>
);

// Overview Stats Card Component
const StatsCard = ({ title, completed, inProgress, icon: Icon, color }) => (
  <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
    <CardContent className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-gray-900">{completed + inProgress}</p>
          <p className="text-xs text-gray-500">Total Projects</p>
        </div>
      </div>
      <h3 className="font-semibold text-gray-800 mb-3">{title}</h3>
      <div className="flex gap-4">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-500" />
          <span className="text-sm text-gray-600">{completed} Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-yellow-500" />
          <span className="text-sm text-gray-600">{inProgress} In Progress</span>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Tutorial Video Component — supports real video/thumbnail URLs
const TutorialCard = ({ title, description, duration, video_url, thumbnail_url, onPlay }) => (
  <Card className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer group overflow-hidden" onClick={() => onPlay && onPlay(video_url)} data-testid="tutorial-card">
    <div className="relative aspect-video bg-gradient-to-br from-gray-800 to-gray-900 overflow-hidden">
      {thumbnail_url && !thumbnail_url.startsWith("data:") ? (
        <img src={thumbnail_url} alt={title} className="absolute inset-0 w-full h-full object-cover" />
      ) : null}
      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
        <div className="w-14 h-14 rounded-full bg-white/25 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
          <Play className="w-7 h-7 text-white ml-1" />
        </div>
      </div>
      {duration && (
        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 rounded text-xs text-white">
          {duration}
        </div>
      )}
      <div className="absolute top-2 left-2 px-2 py-1 bg-blue-500 rounded text-xs text-white font-medium">
        Tutorial
      </div>
    </div>
    <CardContent className="p-4">
      <h4 className="font-semibold text-gray-900 mb-1 truncate">{title}</h4>
      <p className="text-sm text-gray-500 line-clamp-2">{description}</p>
    </CardContent>
  </Card>
);

// Example Project Component — supports real media/thumbnail URLs
const ExampleProjectCard = ({ title, type, media_url, thumbnail_url, description, onClick }) => (
  <Card className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer group overflow-hidden" onClick={() => onClick && onClick({ title, type, media_url, thumbnail_url, description })} data-testid="example-project-card">
    <div className="relative aspect-square bg-gradient-to-br from-purple-100 to-pink-100 overflow-hidden">
      {(thumbnail_url || media_url) && !(thumbnail_url || "").startsWith("data:") ? (
        <img src={thumbnail_url || media_url} alt={title} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <ImageIcon className="w-12 h-12 text-purple-300" />
        </div>
      )}
      {type === "Video" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
          <div className="w-10 h-10 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-5 h-5 text-white ml-0.5" />
          </div>
        </div>
      )}
      <div className="absolute top-2 right-2 px-2 py-1 bg-white/90 rounded text-xs font-medium text-gray-700">
        {type}
      </div>
    </div>
    <CardContent className="p-3">
      <h4 className="font-medium text-gray-900 text-sm truncate">{title}</h4>
      {description && <p className="text-xs text-gray-500 mt-1 line-clamp-1">{description}</p>}
    </CardContent>
  </Card>
);

// Helper: detect if URL is a playable video
const isDirectVideoUrl = (url) => {
  if (!url) return false;
  const lower = url.toLowerCase();
  return lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov') || lower.endsWith('.ogg');
};

// Helper: check if URL is YouTube
const isYouTubeUrl = (url) => {
  if (!url) return false;
  return url.includes('youtube.com') || url.includes('youtu.be');
};

// Helper: extract YouTube embed URL (returns embed URL or wraps in embed for any YT link)
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const watchMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/);
  if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
  const shortMatch = url.match(/youtu\.be\/([^?]+)/);
  if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
  const embedMatch = url.match(/youtube\.com\/embed\/([^?]+)/);
  if (embedMatch) return url;
  return null;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [videoProjects, setVideoProjects] = useState([]);
  const [payments, setPayments] = useState([]);
  const [credits, setCredits] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectImages, setProjectImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null });
  const [projectTypeDialog, setProjectTypeDialog] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [apiUrl, setApiUrl] = useState("");
  const [signedUrls, setSignedUrls] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [plansDialog, setPlansDialog] = useState(false);
  const [forcePlanSelection, setForcePlanSelection] = useState(false);
  const [plans, setPlans] = useState(null);
  const [purchasing, setPurchasing] = useState(null);
  const [adCreatorProjects, setAdCreatorProjects] = useState([]);
  const [advancedStudioProjects, setAdvancedStudioProjects] = useState([]);
  const [imageCreatorProjects, setImageCreatorProjects] = useState([]);
  const [adstudioProjects, setAdstudioProjects] = useState([]);
  const [movieMakerProjects, setMovieMakerProjects] = useState([]);
  const [contentPlannerProjects, setContentPlannerProjects] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  const [expandedSeries, setExpandedSeries] = useState({});
  const [seriesDialog, setSeriesDialog] = useState({ open: false, mode: 'create', series: null });
  const [addToSeriesDialog, setAddToSeriesDialog] = useState({ open: false, projectId: null });
  const [imageCreatorModal, setImageCreatorModal] = useState({ open: false, project: null, images: [] });
  const [adstudioImagesModal, setAdstudioImagesModal] = useState({ open: false, project: null, images: [] });
  const [loadingProjectImages, setLoadingProjectImages] = useState(false);
  const [videoPlayerModal, setVideoPlayerModal] = useState({ open: false, videoUrl: null, projectName: null });
  
  // Showcase content state
  const [tutorials, setTutorials] = useState([]);
  const [exampleProjects, setExampleProjects] = useState([]);
  const [tutorialVideoModal, setTutorialVideoModal] = useState({ open: false, url: null, title: null });
  const [exampleProjectModal, setExampleProjectModal] = useState({ open: false, project: null });

  // User Assets state
  const [userAssets, setUserAssets] = useState({ actors: [], costumes: [], logos: [], products: [] });
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetUploadDialog, setAssetUploadDialog] = useState({ open: false, category: null });
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [assetsExpanded, setAssetsExpanded] = useState(false);
  const [actorDialog, setActorDialog] = useState(false);
  // Voice tooling state for the Actor dialog
  const [actorVoiceFile, setActorVoiceFile] = useState(null);
  const [actorVoiceId, setActorVoiceId] = useState("");
  const [voiceLibraryOpen, setVoiceLibraryOpen] = useState(false);
  const [actorPreview, setActorPreview] = useState(null);
  // Voice editing state for the Actor Preview modal (edit voice on existing actors)
  const [editVoiceFile, setEditVoiceFile] = useState(null);
  const [editVoiceIdInput, setEditVoiceIdInput] = useState("");
  const [editVoiceSaving, setEditVoiceSaving] = useState(false);
  const [editVoiceLibraryOpen, setEditVoiceLibraryOpen] = useState(false);
  // Edit Actor dialog state (full edit)
  const [editActor, setEditActor] = useState(null); // actor being edited
  const [editSaving, setEditSaving] = useState(false);
  const [editRemoveGallery, setEditRemoveGallery] = useState([]); // gs://... URLs marked for removal
  const [editGalleryMode, setEditGalleryMode] = useState("append"); // append | replace
  const [editVoiceFileFull, setEditVoiceFileFull] = useState(null);
  const [editVoiceIdFull, setEditVoiceIdFull] = useState("");
  const [editVoiceLibraryFullOpen, setEditVoiceLibraryFullOpen] = useState(false);
  
  // New state for sidebar
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    loadDashboardData();
    loadSubscription();
    loadRazorpayScript();
    loadUserAssets();
    loadShowcaseContent();
    
    const creditInterval = setInterval(() => {
      refreshCredits();
    }, 30000);
    
    return () => clearInterval(creditInterval);
  }, []);

  // Load showcase tutorials & example projects from Admin Backend
  const loadShowcaseContent = async () => {
    try {
      const [tutRes, projRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/showcase/tutorials`).catch(() => ({ data: { tutorials: [] } })),
        axios.get(`${BACKEND_URL}/api/showcase/example-projects`).catch(() => ({ data: { projects: [] } })),
      ]);
      setTutorials(tutRes.data?.tutorials || []);
      setExampleProjects(projRes.data?.projects || []);
    } catch (e) {
      console.error("Error loading showcase content:", e);
    }
  };

  // Load user assets
  const loadUserAssets = async () => {
    try {
      setAssetsLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/assets`, { withCredentials: true });
      if (response.data?.grouped) {
        setUserAssets(response.data.grouped);
      }
    } catch (error) {
      console.error("Error loading user assets:", error);
    } finally {
      setAssetsLoading(false);
    }
  };

  // Upload asset
  const handleAssetUpload = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    
    if (!assetUploadDialog.category) {
      toast.error("Please select a category");
      return;
    }
    
    formData.append("category", assetUploadDialog.category);
    
    try {
      setUploadingAsset(true);
      const response = await axios.post(`${BACKEND_URL}/api/assets/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (response.data?.success) {
        toast.success("Asset uploaded successfully!");
        setAssetUploadDialog({ open: false, category: null });
        loadUserAssets();
        form.reset();
      }
    } catch (error) {
      console.error("Error uploading asset:", error);
      toast.error(error.response?.data?.detail || "Failed to upload asset");
    } finally {
      setUploadingAsset(false);
    }
  };

  // Delete asset
  const handleDeleteAsset = async (assetId, assetName) => {
    if (!confirm(`Are you sure you want to delete "${assetName}"?`)) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/assets/${assetId}`, { withCredentials: true });
      toast.success("Asset deleted successfully!");
      loadUserAssets();
    } catch (error) {
      console.error("Error deleting asset:", error);
      toast.error("Failed to delete asset");
    }
  };

  // Upload actor with rich metadata
  const handleActorUpload = async (e) => {
    e.preventDefault();
    const form = e.target;
    const fd = new FormData();
    fd.append("name", form.actorName.value);
    fd.append("actor_category", form.actorCategory.value);
    fd.append("gender", form.gender.value);
    fd.append("age_group", form.ageGroup.value);
    fd.append("ethnicity", form.ethnicity.value);
    fd.append("style_type", form.styleType.value);
    fd.append("credits", form.actorCredits.value || "0");
    fd.append("tags", form.tags.value || "");
    fd.append("is_premium", form.isPremium?.checked ? "true" : "false");
    fd.append("default_prompt", form.defaultPrompt?.value || "");
    fd.append("description", form.description?.value || "");
    fd.append("preview_video_url", form.previewVideoUrl?.value || "");
    fd.append("voice_id", actorVoiceId || form.voiceId?.value || "");

    if (form.thumbnail?.files?.[0]) fd.append("thumbnail", form.thumbnail.files[0]);
    // Prefer the recorded/uploaded sample tracked in state (handles Blobs from
    // MediaRecorder). Falls back to legacy file input if state is empty.
    if (actorVoiceFile) {
      fd.append("voice_sample", actorVoiceFile);
    } else if (form.voiceSample?.files?.[0]) {
      fd.append("voice_sample", form.voiceSample.files[0]);
    }
    const galleryInput = form.galleryFiles;
    if (galleryInput?.files) {
      for (let i = 0; i < galleryInput.files.length; i++) {
        fd.append("gallery_files", galleryInput.files[i]);
      }
    }

    try {
      setUploadingAsset(true);
      const response = await axios.post(`${BACKEND_URL}/api/assets/actors`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data?.success) {
        toast.success("Actor created successfully!");
        setActorDialog(false);
        loadUserAssets();
        form.reset();
        setActorVoiceFile(null);
        setActorVoiceId("");
      }
    } catch (error) {
      console.error("Error creating actor:", error);
      toast.error(error.response?.data?.detail || "Failed to create actor");
    } finally {
      setUploadingAsset(false);
    }
  };

  // Open the preview modal in edit-voice-ready state.
  const openActorPreview = (actor) => {
    setActorPreview(actor);
    setEditVoiceFile(null);
    setEditVoiceIdInput(actor?.voice_id || "");
  };

  // Save updated voice_id / voice_sample_url on an existing actor.
  const handleSaveActorVoice = async () => {
    if (!actorPreview?.id) return;
    setEditVoiceSaving(true);
    try {
      const patch = {};

      // If a new sample was recorded/uploaded, push it to GCS first.
      if (editVoiceFile) {
        const fd = new FormData();
        fd.append("file", editVoiceFile);
        const upResp = await axios.post(`${BACKEND_URL}/api/ai/upload-voice-sample`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        const gsUrl = upResp.data?.gs_url;
        if (!gsUrl) throw new Error("Voice sample upload failed");
        patch.voice_sample_url = gsUrl;
      }

      // Persist voice_id if changed.
      if ((editVoiceIdInput || "") !== (actorPreview.voice_id || "")) {
        patch.voice_id = editVoiceIdInput || "";
      }

      if (Object.keys(patch).length === 0) {
        toast.info("Nothing to save");
        return;
      }

      await axios.put(`${BACKEND_URL}/api/assets/${actorPreview.id}`, patch, {
        withCredentials: true,
      });
      toast.success("Actor voice updated");
      setEditVoiceFile(null);
      await loadUserAssets();
      // Refresh the preview modal with the latest actor snapshot
      const refreshed = { ...actorPreview, ...patch };
      setActorPreview(refreshed);
    } catch (err) {
      console.error("Update actor voice failed:", err);
      toast.error(err.response?.data?.detail || err.message || "Failed to save voice");
    } finally {
      setEditVoiceSaving(false);
    }
  };

  // Delete the cloned ElevenLabs voice attached to the currently-previewed actor.
  const handleDeleteActorVoice = async () => {
    const vid = actorPreview?.voice_id;
    if (!vid) return;
    if (!confirm("Delete this cloned voice from ElevenLabs? This frees the slot and cannot be undone.")) return;
    setEditVoiceSaving(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/ai/voice/${vid}`, { withCredentials: true });
      // Backend also unsets voice_id on user_assets — reflect locally
      toast.success("Cloned voice deleted");
      setEditVoiceIdInput("");
      const refreshed = { ...actorPreview, voice_id: "" };
      setActorPreview(refreshed);
      await loadUserAssets();
    } catch (err) {
      console.error("Delete cloned voice failed:", err);
      toast.error(err.response?.data?.detail || err.message || "Failed to delete voice");
    } finally {
      setEditVoiceSaving(false);
    }
  };

  // Open the Edit Actor dialog with the actor prefilled.
  const openEditActor = (actor) => {
    setEditActor(actor);
    setEditRemoveGallery([]);
    setEditGalleryMode("append");
    setEditVoiceFileFull(null);
    setEditVoiceIdFull(actor?.voice_id || "");
  };

  // Submit full actor edits (multipart) to PUT /api/assets/actors/{id}.
  const handleUpdateActor = async (e) => {
    e.preventDefault();
    if (!editActor?.id) return;
    const form = e.target;
    const fd = new FormData();
    fd.append("name", form.actorName.value);
    fd.append("actor_category", form.actorCategory.value);
    fd.append("gender", form.gender.value);
    fd.append("age_group", form.ageGroup.value);
    fd.append("ethnicity", form.ethnicity.value);
    fd.append("style_type", form.styleType.value);
    fd.append("credits", form.actorCredits.value || "0");
    fd.append("tags", form.tags.value || "");
    fd.append("is_premium", form.isPremium?.checked ? "true" : "false");
    fd.append("default_prompt", form.defaultPrompt?.value || "");
    fd.append("description", form.description?.value || "");
    fd.append("preview_video_url", form.previewVideoUrl?.value || "");
    fd.append("voice_id", editVoiceIdFull || "");
    fd.append("gallery_mode", editGalleryMode);
    if (editRemoveGallery.length) {
      fd.append("remove_gallery_indices", editRemoveGallery.join(","));
    }
    if (form.thumbnail?.files?.[0]) fd.append("thumbnail", form.thumbnail.files[0]);
    if (editVoiceFileFull) {
      fd.append("voice_sample", editVoiceFileFull);
    } else if (form.voiceSample?.files?.[0]) {
      fd.append("voice_sample", form.voiceSample.files[0]);
    }
    const galleryInput = form.galleryFiles;
    if (galleryInput?.files) {
      for (let i = 0; i < galleryInput.files.length; i++) {
        fd.append("gallery_files", galleryInput.files[i]);
      }
    }
    setEditSaving(true);
    try {
      const resp = await axios.put(`${BACKEND_URL}/api/assets/actors/${editActor.id}`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (resp.data?.success) {
        toast.success("Actor updated");
        setEditActor(null);
        setEditRemoveGallery([]);
        setEditVoiceFileFull(null);
        setEditVoiceIdFull("");
        await loadUserAssets();
      }
    } catch (err) {
      console.error("Update actor failed:", err);
      toast.error(err.response?.data?.detail || err.message || "Failed to update actor");
    } finally {
      setEditSaving(false);
    }
  };

  // Toggle a gallery image (by index) for removal on save.
  const toggleGalleryRemoval = (idx) => {
    setEditRemoveGallery((prev) => (
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    ));
  };

  const hasToolAccess = (toolName) => {
    if (!subscription || !subscription.tools_access) return false;
    return subscription.tools_access.includes(toolName);
  };

  // ─── Unified app launcher ─────────────────────────────────────────────
  // Enforces two gates in order:
  //   1) Subscription gate — if the user's current plan doesn't include the
  //      app in `tools_access`, show an upgrade toast that opens the Plans
  //      dialog. (Rules confirmed Feb 2026: Free→Adstudio only; Content
  //      Planner→Creator Pack & Studio Pack only; etc.)
  //   2) Credit gate — reuses checkLowCreditsAndProceed to enforce the
  //      per-app credit minimum (100/500/1000/3000 depending on app).
  // Only when both pass does it call handleOpenProject(url).
  const APP_TOOL_KEY = {
    "adstudio": "adstudio",
    "ad-creator": "ad_creator",
    "studio-pro": "advanced_studio",
    "advanced-studio": "advanced_studio",
    "movie-maker": "movie_maker",
    "content-planner": "content_planner",
  };
  const launchApp = (appId, url) => {
    const toolKey = APP_TOOL_KEY[appId];
    const label = APP_LABEL[appId] || appId;
    if (toolKey && !hasToolAccess(toolKey)) {
      toast.error(
        <div className="flex flex-col gap-2" data-testid="plan-upgrade-toast">
          <p className="font-semibold">🔒 Plan Upgrade Required</p>
          <p className="text-sm">
            Your current plan doesn't include <span className="font-semibold">{label}</span>.
            Upgrade your plan to unlock it.
          </p>
          <button
            onClick={() => { toast.dismiss(); loadPlans(); loadSubscription(); setPlansDialog(true); }}
            className="mt-1 self-start px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded text-sm font-medium"
            data-testid="plan-upgrade-view-btn"
          >
            View Plans
          </button>
        </div>,
        { duration: 8000, position: "top-center" }
      );
      return;
    }
    checkLowCreditsAndProceed(() => handleOpenProject(url), appId);
  };

  const loadApiKey = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/integration/api-key`, { withCredentials: true });
      if (response.data?.api_key) {
        setApiKey(response.data.api_key);
        setApiUrl(response.data.api_url || BACKEND_URL);
      }
    } catch (error) {
      console.error("Error loading API key:", error);
    }
  };

  const loadSubscription = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscription`, { withCredentials: true });
      setSubscription(response.data);
    } catch (error) {
      console.error("Error loading subscription:", error);
    }
  };

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/plans`, { withCredentials: true });
      setPlans(response.data.plans);
    } catch (error) {
      console.error("Error loading plans:", error);
    }
  };

  const handlePurchasePlan = useCallback(async (planType) => {
    if (planType === "pay_as_you_go") {
      try {
        setPurchasing(planType);
        const response = await axios.post(
          `${BACKEND_URL}/api/subscription/purchase`,
          { plan_type: planType },
          { withCredentials: true }
        );
        toast.success(response.data.message);
        await loadSubscription();
        await refreshCredits();
        setTimeout(() => setPlansDialog(false), 1000);
      } catch (error) {
        console.error("Error purchasing plan:", error);
        toast.error(error.response?.data?.detail || "Failed to purchase plan");
      } finally {
        setPurchasing(null);
      }
      return;
    }

    try {
      setPurchasing(planType);
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway. Please refresh the page.");
        setPurchasing(null);
        return;
      }

      setPlansDialog(false);
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const orderResponse = await axios.post(
        `${BACKEND_URL}/api/razorpay/create-order`,
        { amount: plans[planType].price, order_type: "plan", plan_type: planType },
        { withCredentials: true }
      );

      const orderData = orderResponse.data;
      const options = {
        key: orderData.key_id,
        amount: orderData.amount_paise,
        currency: orderData.currency,
        name: "LoopAI Creator",
        description: orderData.description,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            const verifyResponse = await axios.post(
              `${BACKEND_URL}/api/razorpay/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { withCredentials: true }
            );
            if (verifyResponse.data.success) {
              toast.success(verifyResponse.data.message);
              if (forcePlanSelection) {
                setForcePlanSelection(false);
                setUser(prev => ({ ...prev, needs_plan_selection: false }));
              }
              await loadSubscription();
              await refreshCredits();
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            toast.error("Payment verification failed. Please contact support.");
          } finally {
            setPurchasing(null);
          }
        },
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#7c3aed" },
        modal: {
          ondismiss: () => {
            setPurchasing(null);
            if (!forcePlanSelection) toast.info("Payment cancelled");
          }
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on("payment.failed", (response) => {
        toast.error(`Payment failed: ${response.error.description}`);
        setPurchasing(null);
      });
      razorpayInstance.open();
    } catch (error) {
      console.error("Error initiating payment:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setPurchasing(null);
    }
  }, [plans, user, forcePlanSelection]);

  const handleSelectInitialPlan = async (planType) => {
    try {
      setPurchasing(planType);
      const response = await axios.post(
        `${BACKEND_URL}/api/subscription/select-plan`,
        { plan_type: planType },
        { withCredentials: true }
      );
      toast.success(response.data.message || "Plan selected successfully!");
      setForcePlanSelection(false);
      setPlansDialog(false);
      await loadSubscription();
      setUser(prev => ({ ...prev, needs_plan_selection: false }));
    } catch (error) {
      console.error("Error selecting plan:", error);
      toast.error(error.response?.data?.detail || "Failed to select plan");
    } finally {
      setPurchasing(null);
    }
  };

  // ─── App credit gating ─────────────────────────────────────────────────
  // Per-app minimum credits required to open each app. Below the minimum we
  // hard-block the launch (no "continue anyway") and route the user to the
  // credit page. Values are the business rules confirmed with Suryakanth
  // on Feb 2026 and must stay in sync between sidebar quick-actions,
  // app-specific tab launchers, and the Create New Project picker.
  const APP_CREDIT_MIN = {
    "content-planner": 3000,
    "adstudio": 100,
    "ad-creator": 500,
    "studio-pro": 1000,
    "advanced-studio": 1000, // alias used in some places for Studio Pro
    "movie-maker": 3000,
  };
  const APP_LABEL = {
    "content-planner": "Content Planner",
    "adstudio": "Adstudio",
    "ad-creator": "Ad Creator",
    "studio-pro": "Studio Pro",
    "advanced-studio": "Studio Pro",
    "movie-maker": "Movie Maker",
  };

  // Signature kept for callsite stability; second arg is now an app id
  // ("adstudio", "ad-creator", "studio-pro"/"advanced-studio", "movie-maker",
  // "content-planner") OR an explicit numeric minimum. When omitted we
  // default to 100 (used by the generic "Create New Project" button that
  // only opens a picker — each tile inside applies its own stricter gate).
  const checkLowCreditsAndProceed = (callback, appOrMin) => {
    let required = 100;
    let appLabel = "this app";
    if (typeof appOrMin === "number") {
      required = appOrMin;
    } else if (typeof appOrMin === "string" && APP_CREDIT_MIN[appOrMin] != null) {
      required = APP_CREDIT_MIN[appOrMin];
      appLabel = APP_LABEL[appOrMin] || appOrMin;
    }
    const current = credits?.credits ?? 0;
    if (current < required) {
      toast.error(
        <div className="flex flex-col gap-2" data-testid="low-credits-block-toast">
          <p className="font-semibold">⚠️ Insufficient Credits</p>
          <p className="text-sm">
            You have <span className="font-bold">{current}</span> credits.
            <br />You need at least <span className="font-bold">{required}</span> credits to open <span className="font-semibold">{appLabel}</span>.
          </p>
          <button
            onClick={() => { toast.dismiss(); navigate("/credits"); }}
            className="mt-1 self-start px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm font-medium"
            data-testid="low-credits-add-btn"
          >
            Add Credits
          </button>
        </div>,
        { duration: 8000, position: "top-center" }
      );
      return;
    }
    callback();
  };

  const getPlanIcon = (planType) => {
    const iconClass = "w-8 h-8";
    switch (planType) {
      case "pay_as_you_go":
        return <svg className={`${iconClass} text-blue-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>;
      case "starter_pack":
        return <svg className={`${iconClass} text-green-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>;
      case "creator_pack":
        return <svg className={`${iconClass} text-yellow-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>;
      case "studio_pack":
        return <svg className={`${iconClass} text-purple-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg>;
      default:
        return null;
    }
  };

  const loadDashboardData = async () => {
    try {
      const userRes = await axios.get(`${BACKEND_URL}/api/auth/session`, { withCredentials: true });
      setUser(userRes.data);
      
      if (userRes.data?.needs_plan_selection) {
        await loadPlans();
        await loadSubscription();
        setForcePlanSelection(true);
        setPlansDialog(true);
      }
      
      const [creditsRes, videoProjectsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/credits`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/video-projects`, { withCredentials: true })
      ]);

      setCredits(creditsRes.data);
      const allVideoProjects = videoProjectsRes.data || [];
      setVideoProjects(allVideoProjects);
      setAdCreatorProjects(allVideoProjects.filter(p => p.project_type === 'ad_creator'));
      // Advanced Studio tab unifies legacy `advanced_studio` rows AND new `studio_pro`
      // rows (saved by studio.loopai.in). Both apps live at the same studio.loopai.in
      // domain, so they share this tab in the dashboard.
      setAdvancedStudioProjects(allVideoProjects.filter(p => p.project_type === 'advanced_studio' || p.project_type === 'studio_pro'));
      setImageCreatorProjects(allVideoProjects.filter(p => p.project_type === 'image_creator'));
      setAdstudioProjects(allVideoProjects.filter(p => p.project_type === 'adstudio'));
      setMovieMakerProjects(allVideoProjects.filter(p => p.project_type === 'movie_maker'));
      setContentPlannerProjects(allVideoProjects.filter(p => p.project_type === 'content_planner'));
      
      // Fetch series/folders
      try {
        const seriesRes = await axios.get(`${BACKEND_URL}/api/series`, { withCredentials: true });
        setSeriesList(seriesRes.data || []);
      } catch (err) {
        console.log("Series fetch skipped:", err.message);
      }
    } catch (error) {
      console.error("Error loading dashboard:", error);
      toast.error("Failed to load dashboard data");
      if (error.response?.status === 401) navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const refreshCredits = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/credits`, { withCredentials: true });
      setCredits(response.data);
    } catch (error) {
      console.error("Error refreshing credits:", error);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects`, { withCredentials: true });
      setProjects(response.data);
    } catch (error) {
      console.error("Error loading projects:", error);
    }
  };

  const loadPayments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments`, { withCredentials: true });
      setPayments(response.data);
    } catch (error) {
      console.error("Error loading payments:", error);
    }
  };

  const handleOpenProject = async (appUrl) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/integration/generate-token`, {}, { withCredentials: true });
      const authToken = response.data.token;
      const separator = appUrl.includes('?') ? '&' : '?';
      window.location.href = `${appUrl}${separator}auth_token=${authToken}`;
    } catch (error) {
      console.error("Error opening project:", error);
      toast.error("Failed to open project");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/");
    }
  };

  const handleDeleteProject = async (projectId, projectType) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/video-projects/${projectId}`, { withCredentials: true });
      toast.success("Project deleted successfully");
      if (projectType === 'ad_creator') {
        setAdCreatorProjects(adCreatorProjects.filter(p => p.id !== projectId));
      } else if (projectType === 'advanced_studio' || projectType === 'studio_pro') {
        // Advanced Studio tab holds both legacy advanced_studio and new studio_pro rows.
        setAdvancedStudioProjects(advancedStudioProjects.filter(p => p.id !== projectId));
      } else if (projectType === 'adstudio') {
        setAdstudioProjects(adstudioProjects.filter(p => p.id !== projectId));
      } else if (projectType === 'movie_maker') {
        setMovieMakerProjects(movieMakerProjects.filter(p => p.id !== projectId));
      } else if (projectType === 'content_planner') {
        setContentPlannerProjects(contentPlannerProjects.filter(p => p.id !== projectId));
      }
      setVideoProjects(videoProjects.filter(p => p.id !== projectId));
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    }
  };

  // Continue with an Adstudio project
  const handleContinueAdstudioProject = async (projectId) => {
    try {
      const tokenResponse = await axios.post(`${BACKEND_URL}/api/integration/generate-token`, {}, {
        withCredentials: true
      });
      const { token } = tokenResponse.data;
      const appUrl = process.env.REACT_APP_ADSTUDIO_URL || 'https://adstudio.loopai.in';
      const url = `${appUrl}?auth_token=${token}&project_id=${projectId}`;
      window.location.href = url;
    } catch (error) {
      console.error("Error generating auth token:", error);
      toast.error("Failed to generate secure authentication. Please try again.");
    }
  };

  // Continue with a video project
  const handleContinueVideoProject = async (projectId) => {
    try {
      const tokenResponse = await axios.post(`${BACKEND_URL}/api/integration/generate-token`, {}, {
        withCredentials: true
      });
      const { token } = tokenResponse.data;
      const appUrl = process.env.REACT_APP_VIDEO_CREATOR_URL || 'https://studio.loopai.in';
      const url = `${appUrl}?auth_token=${token}&project_id=${projectId}`;
      window.location.href = url;
    } catch (error) {
      console.error("Error generating auth token:", error);
      toast.error("Failed to generate secure authentication. Please try again.");
    }
  };

  // Continue with a Content Planner project — opens planner.loopai.in
  // with a session-exchange auth_token and the project_id pre-loaded.
  const handleContinueContentPlannerProject = async (projectId) => {
    try {
      const tokenResponse = await axios.post(`${BACKEND_URL}/api/integration/generate-token`, {}, {
        withCredentials: true
      });
      const { token } = tokenResponse.data;
      const appUrl = process.env.REACT_APP_CONTENT_PLANNER_URL || 'https://planner.loopai.in';
      window.location.href = `${appUrl}?auth_token=${token}&project_id=${projectId}`;
    } catch (error) {
      console.error("Error generating auth token:", error);
      toast.error("Failed to open project. Please try again.");
    }
  };

  // Continue with an ad creator project
  const handleContinueAdCreatorProject = async (projectId) => {
    try {
      const tokenResponse = await axios.post(`${BACKEND_URL}/api/integration/generate-token`, {}, {
        withCredentials: true
      });
      const { token } = tokenResponse.data;
      const appUrl = process.env.REACT_APP_AD_CREATOR_URL || 'https://ads.loopai.in';
      const url = `${appUrl}?auth_token=${token}&project_id=${projectId}`;
      window.location.href = url;
    } catch (error) {
      console.error("Error generating auth token:", error);
      toast.error("Failed to generate secure authentication. Please try again.");
    }
  };

  // View Image Creator Project
  const handleViewImageCreatorProject = async (project) => {
    setLoadingProjectImages(true);
    setImageCreatorModal({ open: true, project, images: [] });
    
    try {
      const projectData = project.project_data || {};
      let images = [];
      
      if (projectData.generated_images) {
        images = projectData.generated_images;
      } else if (projectData.images) {
        images = projectData.images;
      } else if (projectData.results) {
        images = projectData.results.filter(r => r.url || r.image_url).map(r => ({
          url: r.url || r.image_url,
          prompt: r.prompt || ''
        }));
      }
      
      if (project.images && project.images.length > 0) {
        images = [...images, ...project.images];
      }
      
      setImageCreatorModal(prev => ({ ...prev, images }));
    } catch (error) {
      console.error("Error loading project images:", error);
      toast.error("Failed to load project images");
    } finally {
      setLoadingProjectImages(false);
    }
  };

  // View Adstudio Project Images
  // The dashboard list endpoint excludes project_data for size, so we fetch
  // the full project record from /api/video-projects/{id} on click — that
  // endpoint returns project_data with all gs:// URIs already re-signed.
  const handleViewAdstudioImages = async (project) => {
    setLoadingProjectImages(true);
    setAdstudioImagesModal({ open: true, project, images: [] });

    try {
      let projectData = project.project_data || null;
      let topLevel = project;

      // If we don't have project_data from the list (we won't, it's excluded),
      // fetch the canonical full record. This returns project_data with all
      // gs:// URIs converted to fresh 7-day signed URLs.
      if (!projectData || Object.keys(projectData).length === 0) {
        const resp = await axios.get(
          `${BACKEND_URL}/api/video-projects/${project.id}`,
          { withCredentials: true }
        );
        topLevel = resp.data || {};
        projectData = topLevel.project_data || {};
      }

      const images = [];
      const seen = new Set();
      const pushImg = (img, extra = {}) => {
        const url = typeof img === 'string' ? img : (img && (img.url || img.image_url || img.gcsUrl || img.signed_url || img.src));
        if (!url || seen.has(url)) return;
        seen.add(url);
        images.push({
          url,
          prompt: (img && img.prompt) || extra.prompt || '',
          concept: extra.concept || (img && img.concept) || '',
        });
      };

      // Standard locations the various Adstudio/image flows write to.
      if (Array.isArray(projectData.generatedImages)) projectData.generatedImages.forEach(i => pushImg(i));
      if (Array.isArray(projectData.generated_images)) projectData.generated_images.forEach(i => pushImg(i));
      if (Array.isArray(projectData.images)) projectData.images.forEach(i => pushImg(i));
      if (Array.isArray(projectData.results)) projectData.results.forEach(r => pushImg(r));

      // Ad-concept flows: each concept may carry its own images array.
      const concepts = projectData.adConcepts || projectData.ad_concepts;
      if (Array.isArray(concepts)) {
        concepts.forEach(c => {
          if (Array.isArray(c.images)) c.images.forEach(i => pushImg(i, { concept: c.title || c.name }));
        });
      }

      // Ad Studio's image flow stores final generated images as image-type
      // assets on the project: `assets: [{id, type:"image", name, data:<url>,
      // briefId, briefPurpose, ...}]`. The `data` field is the signed URL.
      // This is the primary location for adstudio image-flow projects.
      if (Array.isArray(projectData.assets)) {
        projectData.assets.forEach(a => {
          if (a && a.type === 'image' && a.data) {
            pushImg({ url: a.data, prompt: a.briefPurpose || '', concept: a.name || '' });
          }
        });
      }

      // Image-script flow: image_briefs[].generated_image_url. Two shapes
      // exist in the wild: nested under image_set, or hoisted to root.
      const imageSet = projectData.imageScript?.image_set || projectData.image_set;
      const candidates = [
        imageSet?.image_briefs,
        projectData.imageScript?.image_briefs,
        projectData.image_briefs,
      ].filter(Array.isArray);
      candidates.forEach(arr => {
        arr.forEach(b => {
          const u = b.generated_image_url || b.image_url || b.url;
          if (u) pushImg({ url: u, prompt: b.scene || b.description || b.title || '' });
        });
      });

      // Top-level fallback (rare).
      if (Array.isArray(topLevel.images)) topLevel.images.forEach(i => pushImg(i));

      // Last-resort defensive fallback: recursively walk project_data and
      // collect any string that LOOKS like an image URL. This catches any
      // shape Ad Studio (or future flows) might use that we haven't
      // explicitly mapped above — e.g. nested under
      // `scenes[].imageUrl`, `briefs[].generated_url`, `assets[].data`, etc.
      // We only fire this fallback if the structured extraction returned 0
      // images, so it never duplicates or pollutes the legitimate paths.
      if (images.length === 0) {
        const isImageUrl = (s) => {
          if (typeof s !== 'string') return false;
          if (!s.startsWith('http')) return false;
          // GCS image bucket OR common image extensions
          return (
            s.includes('storage.googleapis.com/emergent-images') ||
            s.includes('storage.googleapis.com/emergent-image') ||
            /\.(png|jpe?g|webp|gif|avif)(\?|$)/i.test(s)
          );
        };
        const walk = (node) => {
          if (!node) return;
          if (typeof node === 'string') {
            if (isImageUrl(node)) pushImg(node);
            return;
          }
          if (Array.isArray(node)) {
            node.forEach(walk);
            return;
          }
          if (typeof node === 'object') {
            Object.values(node).forEach(walk);
          }
        };
        walk(projectData);
        if (images.length > 0) {
          console.info(`[adstudio-modal] Structured extraction found 0 images, recursive fallback recovered ${images.length}`);
        }
      }

      setAdstudioImagesModal(prev => ({ ...prev, project: { ...project, project_data: projectData }, images }));
    } catch (error) {
      console.error("Error loading Adstudio images:", error);
      toast.error("Failed to load project images");
    } finally {
      setLoadingProjectImages(false);
    }
  };

  // Download single image
  const handleDownloadImage = async (imageUrl, filename) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'image.png';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Image downloaded!");
    } catch (error) {
      console.error("Error downloading image:", error);
      toast.error("Failed to download image");
    }
  };

  // Download all images from a project
  const handleDownloadAllImages = async (images, projectName) => {
    if (!images || images.length === 0) {
      toast.error("No images to download");
      return;
    }
    
    toast.info(`Downloading ${images.length} image(s)...`);
    
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      const url = img.url || img.image_url || img;
      const filename = `${projectName || 'project'}_image_${i + 1}.png`;
      await handleDownloadImage(url, filename);
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    toast.success("All images downloaded!");
  };

  // Share image
  const handleShareImage = async (imageUrl) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Check out this AI-generated image!',
          url: imageUrl
        });
      } else {
        await navigator.clipboard.writeText(imageUrl);
        toast.success("Image URL copied to clipboard!");
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(imageUrl);
          toast.success("Image URL copied to clipboard!");
        } catch (clipError) {
          toast.error("Failed to share image");
        }
      }
    }
  };

  // Download video
  const handleDownloadVideo = async (videoUrl, projectName, projectId) => {
    try {
      toast.info("Preparing download...");
      
      let downloadUrl = videoUrl;
      
      if (projectId) {
        try {
          const signedUrlResponse = await axios.get(
            `${BACKEND_URL}/api/video-projects/${projectId}/rendered-video-url`,
            { withCredentials: true }
          );
          downloadUrl = signedUrlResponse.data.signed_url;
        } catch (error) {
          console.warn('Could not fetch fresh signed URL, using stored URL:', error);
        }
      }
      
      toast.info("Downloading video...");
      
      const response = await fetch(downloadUrl);
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${projectName}.mp4`;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success("Video downloaded successfully!");
    } catch (error) {
      console.error("Error downloading video:", error);
      toast.error(`Failed to download video: ${error.message}`);
    }
  };

  // View video in modal
  const handleViewVideo = async (videoUrl, projectName, projectId) => {
    try {
      let playUrl = videoUrl;
      
      // Try to get a fresh signed URL
      if (projectId) {
        try {
          const signedUrlResponse = await axios.get(
            `${BACKEND_URL}/api/video-projects/${projectId}/rendered-video-url`,
            { withCredentials: true }
          );
          playUrl = signedUrlResponse.data.signed_url;
        } catch (error) {
          console.warn('Could not fetch fresh signed URL, using stored URL:', error);
        }
      }
      
      setVideoPlayerModal({
        open: true,
        videoUrl: playUrl,
        projectName: projectName
      });
    } catch (error) {
      console.error("Error opening video:", error);
      toast.error("Failed to load video");
    }
  };

  // Calculate stats for overview
  const getProjectStats = () => {
    const imageCreatorCompleted = imageCreatorProjects.filter(p => p.status === 'completed').length;
    const imageCreatorInProgress = imageCreatorProjects.filter(p => p.status !== 'completed').length;
    const adstudioCompleted = adstudioProjects.filter(p => p.status === 'completed').length;
    const adstudioInProgress = adstudioProjects.filter(p => p.status !== 'completed').length;
    const movieMakerCompleted = movieMakerProjects.filter(p => p.status === 'completed').length;
    const movieMakerInProgress = movieMakerProjects.filter(p => p.status !== 'completed').length;
    const adCreatorCompleted = adCreatorProjects.filter(p => p.status === 'completed').length;
    const adCreatorInProgress = adCreatorProjects.filter(p => p.status !== 'completed').length;
    const advancedStudioCompleted = advancedStudioProjects.filter(p => p.status === 'completed').length;
    const advancedStudioInProgress = advancedStudioProjects.filter(p => p.status !== 'completed').length;
    const contentPlannerCompleted = contentPlannerProjects.filter(p => p.status === 'completed').length;
    const contentPlannerInProgress = contentPlannerProjects.filter(p => p.status !== 'completed').length;

    return {
      imageCreator: { completed: imageCreatorCompleted, inProgress: imageCreatorInProgress },
      adstudio: { completed: adstudioCompleted, inProgress: adstudioInProgress },
      adCreator: { completed: adCreatorCompleted, inProgress: adCreatorInProgress },
      advancedStudio: { completed: advancedStudioCompleted, inProgress: advancedStudioInProgress },
      movieMaker: { completed: movieMakerCompleted, inProgress: movieMakerInProgress },
      contentPlanner: { completed: contentPlannerCompleted, inProgress: contentPlannerInProgress }
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const stats = getProjectStats();

  // Asset category config
  const assetCategories = [
    { id: "actors", label: "Actors", icon: Users },
    { id: "costumes", label: "Costumes", icon: Shirt },
    { id: "logos", label: "Logos", icon: Tag },
    { id: "products", label: "Products", icon: Package },
  ];

  // Sidebar navigation items
  const sidebarItems = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "brands-products", label: "Brands & Products", icon: Building2 },
    { id: "content-planner", label: "Content Planner", icon: Calendar, locked: !hasToolAccess('content_planner') },
    { id: "adstudio", label: "Adstudio", icon: BookImage },
    { id: "ad-creator", label: "Ad Creator", icon: Video, locked: !hasToolAccess('ad_creator') },
    { id: "advanced-studio", label: "Studio Pro", icon: Film, locked: !hasToolAccess('advanced_studio') },
    { id: "movie-maker", label: "Movie Maker", icon: Clapperboard, locked: !hasToolAccess('movie_maker') },
    { id: "payments", label: "Payments", icon: Wallet },
  ];

  // Reusable movie project card renderer
  const renderMovieCard = (project, episodeNumber, seriesId) => {
    const projectData = project.project_data || {};
    const hasVideo = !!(project.rendered_video_url || projectData.video_url || projectData.rendered_video_url);
    const isCompleted = project.status === 'completed' || hasVideo;
    const thumbnail = project.thumbnail || projectData.thumbnail || projectData.cover_image || null;

    return (
      <Card key={project.id} className="border hover:border-orange-400 hover:shadow-md transition-all duration-200" data-testid={`movie-card-${project.id}`}>
        <CardHeader className="pb-2 px-4 pt-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <CardTitle className="text-sm mb-1 flex items-center gap-2 truncate">
                {episodeNumber != null && (
                  <span className="shrink-0 w-6 h-6 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center font-bold">
                    {episodeNumber}
                  </span>
                )}
                <Clapperboard className="w-4 h-4 text-orange-500 shrink-0" />
                <span className="truncate">{project.name || 'Untitled Movie'}</span>
              </CardTitle>
              <p className="text-xs text-gray-500">{new Date(project.last_modified || project.created_at).toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {!seriesId && seriesList.length > 0 && (
                <Button variant="ghost" size="sm" title="Add to series"
                  onClick={() => setAddToSeriesDialog({ open: true, projectId: project.id })}
                  className="text-gray-400 hover:text-orange-500 h-7 w-7 p-0"
                >
                  <FolderOpen className="w-3.5 h-3.5" />
                </Button>
              )}
              {seriesId && (
                <Button variant="ghost" size="sm" title="Remove from series"
                  onClick={async () => {
                    try {
                      await axios.delete(`${BACKEND_URL}/api/series/${seriesId}/episodes/${project.id}`, { withCredentials: true });
                      toast.success("Removed from series");
                      try {
                        const seriesRes = await axios.get(`${BACKEND_URL}/api/series`, { withCredentials: true });
                        setSeriesList(seriesRes.data || []);
                      } catch (e) { /* ignore */ }
                    } catch (err) { toast.error("Failed to remove"); }
                  }}
                  className="text-gray-400 hover:text-orange-500 h-7 w-7 p-0"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button variant="ghost" size="sm"
                onClick={() => setDeleteDialog({ open: true, type: 'movie_maker', id: project.id })}
                className="text-red-400 hover:text-red-600 h-7 w-7 p-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium mt-1 w-fit ${
            isCompleted ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
          }`}>
            {isCompleted ? <><CheckCircle2 className="w-3 h-3" /> Completed</> : <><Clock className="w-3 h-3" /> In Progress</>}
          </span>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-2 space-y-2">
          {thumbnail ? (
            <img src={thumbnail} alt={project.name} className="w-full h-28 object-cover rounded-lg" />
          ) : (
            <div className="w-full h-28 bg-gradient-to-br from-orange-50 to-red-50 rounded-lg flex items-center justify-center">
              <Clapperboard className="w-10 h-10 text-orange-200" />
            </div>
          )}
          {hasVideo && (
            <Button size="sm" variant="outline" className="w-full text-xs border-orange-300 text-orange-600 hover:bg-orange-50 h-8"
              onClick={() => setVideoPlayerModal({ open: true, videoUrl: project.rendered_video_url || projectData.video_url, projectName: project.name })}
            >
              <Play className="w-3 h-3 mr-1" /> Play Movie
            </Button>
          )}
          <Button size="sm" className="w-full text-xs bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 h-8"
            onClick={async () => {
              try {
                const res = await axios.post(`${BACKEND_URL}/api/integration/generate-token`, {}, { withCredentials: true });
                const token = res.data.token;
                const movieStudioUrl = process.env.REACT_APP_MOVIE_MAKER_URL || 'https://moviestudio.loopai.in';
                window.location.href = `${movieStudioUrl}?auth_token=${token}&project_id=${project.id}`;
              } catch (e) { toast.error("Failed to open project"); }
            }}
          >
            <ExternalLink className="w-3 h-3 mr-1" /> Open in Movie Studio
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 flex flex-col transition-all duration-300 fixed h-full z-40`}>
        {/* Logo */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                LoopAI Creator
              </h1>
            )}
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {sidebarOpen ? <X className="w-5 h-5 text-gray-500" /> : <Menu className="w-5 h-5 text-gray-500" />}
            </button>
          </div>
        </div>

        {/* User Info */}
        {sidebarOpen && (
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                  {user?.name?.charAt(0) || user?.email?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name || "User"}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {sidebarItems.map(item => (
            <SidebarItem
              key={item.id}
              icon={item.icon}
              label={sidebarOpen ? item.label : ""}
              isActive={activeTab === item.id}
              locked={item.locked}
              onClick={() => {
                if (item.locked) {
                  toast.info(`Upgrade your plan to access ${item.label}`);
                  loadPlans();
                  loadSubscription();
                  setPlansDialog(true);
                } else {
                  setActiveTab(item.id);
                  if (item.id === 'saved-images') loadProjects();
                  if (item.id === 'payments') loadPayments();
                }
              }}
            />
          ))}
          
          {/* User Assets Section */}
          <div className="mt-2 pt-2 border-t border-gray-100">
            <button
              onClick={() => setAssetsExpanded(!assetsExpanded)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeTab.startsWith('assets-') ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FolderOpen className="w-5 h-5" />
              {sidebarOpen && (
                <>
                  <span className="flex-1 text-left font-medium">User Assets</span>
                  {assetsExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </>
              )}
            </button>
            
            {assetsExpanded && sidebarOpen && (
              <div className="ml-4 space-y-1 mt-1">
                {assetCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveTab(`assets-${cat.id}`)}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-all ${
                      activeTab === `assets-${cat.id}` ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <cat.icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{cat.label}</span>
                    <span className="text-xs bg-gray-200 px-2 py-0.5 rounded-full">
                      {userAssets[cat.id]?.length || 0}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Credits & Logout */}
        <div className="p-3 border-t border-gray-100 space-y-2">
          <div
            onClick={() => navigate("/credits")}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all ${
              (credits?.credits || 0) < 500
                ? "bg-gradient-to-r from-red-500 to-red-600 text-white animate-pulse"
                : "bg-gradient-to-r from-blue-500 to-purple-600 text-white"
            }`}
          >
            <CreditCard className="w-5 h-5" />
            {sidebarOpen && (
              <div>
                <p className="font-bold">{credits?.credits || 0} Credits</p>
                {(credits?.credits || 0) < 500 && <p className="text-xs opacity-90">Low Balance!</p>}
              </div>
            )}
          </div>
          
          <Button
            variant="outline"
            onClick={() => { loadPlans(); loadSubscription(); setPlansDialog(true); }}
            className="w-full justify-start gap-3"
          >
            <Crown className="w-5 h-5 text-yellow-500" />
            {sidebarOpen && <span>View Plans</span>}
          </Button>

          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start gap-3 text-gray-600 hover:text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 min-w-0 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-8">
              {/* Welcome Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Welcome back, {user?.name?.split(' ')[0] || 'Creator'}!</h1>
                  <p className="text-gray-600 mt-1">Here's an overview of your creative projects</p>
                </div>
                <Button
                  onClick={() => checkLowCreditsAndProceed(() => setProjectTypeDialog(true))}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Project
                </Button>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                <StatsCard
                  title="Content Planner"
                  completed={stats.contentPlanner.completed}
                  inProgress={stats.contentPlanner.inProgress}
                  icon={Calendar}
                  color="bg-gradient-to-br from-blue-500 to-cyan-500"
                />
                <StatsCard
                  title="Adstudio"
                  completed={stats.adstudio.completed}
                  inProgress={stats.adstudio.inProgress}
                  icon={BookImage}
                  color="bg-gradient-to-br from-teal-500 to-cyan-500"
                />
                <StatsCard
                  title="Ad Creator"
                  completed={stats.adCreator.completed}
                  inProgress={stats.adCreator.inProgress}
                  icon={Video}
                  color="bg-gradient-to-br from-purple-500 to-pink-500"
                />
                <StatsCard
                  title="Studio Pro"
                  completed={stats.advancedStudio.completed}
                  inProgress={stats.advancedStudio.inProgress}
                  icon={Film}
                  color="bg-gradient-to-br from-pink-500 to-rose-500"
                />
                <StatsCard
                  title="Movie Maker"
                  completed={stats.movieMaker.completed}
                  inProgress={stats.movieMaker.inProgress}
                  icon={Clapperboard}
                  color="bg-gradient-to-br from-orange-500 to-red-500"
                />
              </div>

              {/* Tutorial Videos Section */}
              {tutorials.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Tutorial Videos</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {tutorials.map(t => (
                    <TutorialCard
                      key={t.id}
                      title={t.title}
                      description={t.description}
                      duration={t.duration}
                      video_url={t.video_url}
                      thumbnail_url={t.thumbnail_url}
                      onPlay={(url) => {
                        if (!url) return;
                        const ytEmbed = getYouTubeEmbedUrl(url);
                        if (ytEmbed) {
                          setTutorialVideoModal({ open: true, url: ytEmbed, title: t.title, type: 'youtube' });
                        } else if (isDirectVideoUrl(url)) {
                          setTutorialVideoModal({ open: true, url, title: t.title, type: 'video' });
                        } else {
                          window.open(url, '_blank');
                        }
                      }}
                    />
                  ))}
                </div>
              </div>
              )}

              {/* Example Projects Section */}
              {exampleProjects.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Example Projects</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {exampleProjects.map(p => (
                    <ExampleProjectCard
                      key={p.id}
                      title={p.title}
                      type={p.type}
                      description={p.description}
                      media_url={p.media_url}
                      thumbnail_url={p.thumbnail_url}
                      onClick={(proj) => setExampleProjectModal({ open: true, project: proj })}
                    />
                  ))}
                </div>
              </div>
              )}

              {/* Quick Actions */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2 bg-white hover:bg-blue-50 border-2"
                    onClick={() => launchApp("content-planner", process.env.REACT_APP_CONTENT_PLANNER_URL || 'https://planner.loopai.in')}
                    data-testid="quick-action-content-planner"
                  >
                    <Calendar className="w-6 h-6 text-blue-500" />
                    <span>Plan Content</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2 bg-white hover:bg-purple-50 border-2"
                    onClick={() => launchApp("ad-creator", process.env.REACT_APP_AD_CREATOR_URL || 'https://ads.loopai.in')}
                  >
                    <Video className="w-6 h-6 text-purple-500" />
                    <span>Create Ad</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2 bg-white hover:bg-pink-50 border-2"
                    onClick={() => launchApp("studio-pro", process.env.REACT_APP_VIDEO_CREATOR_URL || 'https://studio.loopai.in')}
                  >
                    <Film className="w-6 h-6 text-pink-500" />
                    <span>Studio Pro</span>
                    {!hasToolAccess('advanced_studio') && <Crown className="w-4 h-4 text-yellow-500" />}
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col gap-2 bg-white hover:bg-orange-50 border-2"
                    onClick={() => launchApp("movie-maker", process.env.REACT_APP_MOVIE_MAKER_URL || 'https://moviestudio.loopai.in')}
                  >
                    <Clapperboard className="w-6 h-6 text-orange-500" />
                    <span>Movie Maker</span>
                    {!hasToolAccess('movie_maker') && <Crown className="w-4 h-4 text-yellow-500" />}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Product Images Tab */}
          {activeTab === "brands-products" && <BrandsAndProducts />}

          {activeTab === "content-planner" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Content Planner</h1>
                  <p className="text-gray-600">Plan your weekly &amp; monthly content for your business or brand</p>
                </div>
                <Button
                  onClick={() => launchApp("content-planner", process.env.REACT_APP_CONTENT_PLANNER_URL || 'https://planner.loopai.in')}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                  data-testid="content-planner-create-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Project
                </Button>
              </div>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  {contentPlannerProjects.length === 0 ? (
                    <div className="text-center py-12" data-testid="content-planner-empty-state">
                      <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No content plans yet</h3>
                      <p className="text-gray-500 mb-6">Build a weekly or monthly content calendar for your brand</p>
                      <Button
                        onClick={() => launchApp("content-planner", process.env.REACT_APP_CONTENT_PLANNER_URL || 'https://planner.loopai.in')}
                        className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                        data-testid="content-planner-create-first-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Your First Plan
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {contentPlannerProjects.map(project => (
                        <Card key={project.id} className="border-2 hover:border-blue-400 hover:shadow-lg transition-all duration-200" data-testid={`content-planner-card-${project.id}`}>
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <CardTitle className="text-lg mb-1 flex items-center gap-2">
                                  <Calendar className="w-5 h-5 text-blue-500" />
                                  {project.name || 'Untitled Plan'}
                                </CardTitle>
                                <p className="text-xs text-gray-500">
                                  Last modified: {project.last_modified ? new Date(project.last_modified).toLocaleDateString() : new Date(project.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteProject(project.id, 'content_planner');
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`content-planner-delete-${project.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="mt-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                project.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {project.status === 'completed' ? (
                                  <><CheckCircle2 className="w-3 h-3" /> Completed</>
                                ) : (
                                  <><Clock className="w-3 h-3" /> In Progress</>
                                )}
                              </span>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-2">
                            <Button
                              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-sm"
                              onClick={() => handleContinueContentPlannerProject(project.id)}
                              data-testid={`content-planner-continue-${project.id}`}
                            >
                              <Play className="w-4 h-4 mr-2" />
                              {project.status === 'completed' ? 'View Plan' : 'Continue with Project'}
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Ad Creator Tab */}
          {activeTab === "ad-creator" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Ad Creator</h1>
                  <p className="text-gray-600">Create engaging promotional videos with AI</p>
                </div>
                <Button
                  onClick={() => launchApp("ad-creator", process.env.REACT_APP_AD_CREATOR_URL || 'https://ads.loopai.in')}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Ad
                </Button>
              </div>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  {adCreatorProjects.length === 0 ? (
                    <div className="text-center py-12">
                      <Video className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No ad projects yet</h3>
                      <p className="text-gray-500 mb-6">Create your first promotional video</p>
                      <Button
                        onClick={() => launchApp("ad-creator", process.env.REACT_APP_AD_CREATOR_URL || 'https://ads.loopai.in')}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Your First Ad
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {adCreatorProjects.map(project => (
                        <Card key={project.id} className="border-2 hover:border-purple-400 hover:shadow-lg transition-all duration-200" data-testid={`ad-project-card-${project.id}`}>
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <CardTitle className="text-lg mb-1 flex items-center gap-2">
                                  <Video className="w-5 h-5 text-purple-500" />
                                  {project.name || 'Untitled'}
                                </CardTitle>
                                <p className="text-xs text-gray-500">
                                  Last modified: {new Date(project.last_modified || project.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setDeleteDialog({ open: true, type: 'ad_creator', id: project.id });
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="mt-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                project.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {project.status === 'completed' ? (
                                  <><CheckCircle2 className="w-3 h-3" /> Completed</>
                                ) : (
                                  <><Clock className="w-3 h-3" /> In Progress</>
                                )}
                              </span>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-2">
                            {project.status === 'in_progress' || project.status !== 'completed' ? (
                              <Button
                                onClick={() => handleContinueAdCreatorProject(project.id)}
                                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                                data-testid={`continue-project-${project.id}`}
                              >
                                <Play className="w-4 h-4 mr-2" />
                                Continue with Project
                              </Button>
                            ) : (
                              <div className="space-y-2">
                                {project.rendered_video_url && (
                                  <>
                                    <Button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewVideo(project.rendered_video_url, project.name, project.id);
                                      }}
                                      className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                                      data-testid={`view-video-${project.id}`}
                                    >
                                      <Play className="w-4 h-4 mr-2" />
                                      View Video
                                    </Button>
                                    <Button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadVideo(project.rendered_video_url, project.name, project.id);
                                      }}
                                      className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                                      data-testid={`download-video-${project.id}`}
                                    >
                                      <Download className="w-4 h-4 mr-2" />
                                      Download Rendered Video
                                    </Button>
                                  </>
                                )}
                                <Button
                                  onClick={() => handleContinueAdCreatorProject(project.id)}
                                  variant="outline"
                                  className="w-full border-purple-300 text-purple-600 hover:bg-purple-50"
                                  data-testid={`view-project-${project.id}`}
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Project
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Advanced Studio Tab */}
          {activeTab === "advanced-studio" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Studio Pro</h1>
                  <p className="text-gray-600">Complete creative control for video editing</p>
                </div>
                <Button
                  onClick={() => launchApp("studio-pro", process.env.REACT_APP_VIDEO_CREATOR_URL || 'https://studio.loopai.in')}
                  className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Open Studio
                </Button>
              </div>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  {advancedStudioProjects.length === 0 ? (
                    <div className="text-center py-12">
                      <Film className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No studio projects yet</h3>
                      <p className="text-gray-500 mb-6">Start creating with full creative control</p>
                      <Button
                        onClick={() => launchApp("studio-pro", process.env.REACT_APP_VIDEO_CREATOR_URL || 'https://studio.loopai.in')}
                        className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Open Studio Pro
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {advancedStudioProjects.map(project => (
                        <Card key={project.id} className="border-2 hover:border-pink-400 hover:shadow-lg transition-all duration-200" data-testid={`studio-project-card-${project.id}`}>
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <CardTitle className="text-lg mb-1 flex items-center gap-2">
                                  <Film className="w-5 h-5 text-pink-500" />
                                  {project.name || 'Untitled'}
                                </CardTitle>
                                <p className="text-xs text-gray-500">
                                  Last modified: {new Date(project.last_modified || project.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setDeleteDialog({ open: true, type: 'advanced_studio', id: project.id });
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="mt-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                project.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {project.status === 'completed' ? (
                                  <><CheckCircle2 className="w-3 h-3" /> Completed</>
                                ) : (
                                  <><Clock className="w-3 h-3" /> In Progress</>
                                )}
                              </span>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-2">
                            {project.status === 'in_progress' || project.status !== 'completed' ? (
                              <Button
                                onClick={() => handleContinueVideoProject(project.id)}
                                className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
                                data-testid={`continue-project-${project.id}`}
                              >
                                <Play className="w-4 h-4 mr-2" />
                                Continue with Project
                              </Button>
                            ) : (
                              <div className="space-y-2">
                                {project.rendered_video_url && (
                                  <>
                                    <Button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewVideo(project.rendered_video_url, project.name, project.id);
                                      }}
                                      className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                                      data-testid={`view-video-${project.id}`}
                                    >
                                      <Play className="w-4 h-4 mr-2" />
                                      View Video
                                    </Button>
                                    <Button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadVideo(project.rendered_video_url, project.name, project.id);
                                      }}
                                      className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                                      data-testid={`download-video-${project.id}`}
                                    >
                                      <Download className="w-4 h-4 mr-2" />
                                      Download Rendered Video
                                    </Button>
                                  </>
                                )}
                                <Button
                                  onClick={() => handleContinueVideoProject(project.id)}
                                  variant="outline"
                                  className="w-full border-pink-300 text-pink-600 hover:bg-pink-50"
                                  data-testid={`view-project-${project.id}`}
                                >
                                  <Eye className="w-4 h-4 mr-2" />
                                  View Project
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Adstudio Tab */}
          {activeTab === "adstudio" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Adstudio</h1>
                  <p className="text-gray-600">Generate high quality images for product catalogs, ads, social media & brand identity</p>
                </div>
                <Button
                  onClick={() => launchApp("adstudio", process.env.REACT_APP_ADSTUDIO_URL || 'https://adstudio.loopai.in')}
                  className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Images
                </Button>
              </div>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  {adstudioProjects.length === 0 ? (
                    <div className="text-center py-12">
                      <BookImage className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No Adstudio projects yet</h3>
                      <p className="text-gray-500 mb-6">Create high quality images for your products and brand</p>
                      <Button
                        onClick={() => launchApp("adstudio", process.env.REACT_APP_ADSTUDIO_URL || 'https://adstudio.loopai.in')}
                        className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Your First Project
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {adstudioProjects.map(project => (
                        <Card key={project.id} className="border-2 hover:border-teal-400 hover:shadow-lg transition-all duration-200" data-testid={`adstudio-project-card-${project.id}`}>
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <CardTitle className="text-lg mb-1 flex items-center gap-2">
                                  <BookImage className="w-5 h-5 text-teal-500" />
                                  {project.name || 'Untitled'}
                                </CardTitle>
                                <p className="text-xs text-gray-500">
                                  Last modified: {new Date(project.last_modified || project.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setDeleteDialog({ open: true, type: 'adstudio', id: project.id });
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="mt-2 flex items-center gap-3">
                              {(() => {
                                // Check if project has images (treat as completed)
                                const projectData = project.project_data || {};
                                const hasImages = (
                                  (projectData.generatedImages && projectData.generatedImages.length > 0) ||
                                  (projectData.generated_images && projectData.generated_images.length > 0) ||
                                  (projectData.images && projectData.images.length > 0) ||
                                  (project.images && project.images.length > 0)
                                );
                                const isCompleted = project.status === 'completed' || hasImages;
                                
                                // Get credits used from various possible locations
                                const creditsUsed = projectData.creditsUsed || projectData.credits_used || project.credits_used || 0;
                                
                                // Count number of images
                                const imageCount = (projectData.generatedImages?.length || 0) + 
                                                   (projectData.generated_images?.length || 0) + 
                                                   (projectData.images?.length || 0) +
                                                   (project.images?.length || 0);
                                
                                return (
                                  <>
                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                      isCompleted ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                    }`}>
                                      {isCompleted ? (
                                        <><CheckCircle2 className="w-3 h-3" /> Completed</>
                                      ) : (
                                        <><Clock className="w-3 h-3" /> In Progress</>
                                      )}
                                    </span>
                                    
                                    {/* Show credits used if available */}
                                    {creditsUsed > 0 && (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                        <Sparkles className="w-3 h-3" />
                                        {creditsUsed} credits
                                      </span>
                                    )}
                                    
                                    {/* Show image count if completed */}
                                    {isCompleted && imageCount > 0 && (
                                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                        <ImageIcon className="w-3 h-3" />
                                        {imageCount} image{imageCount > 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </CardHeader>
                          <CardContent className="pt-2">
                            {(() => {
                              // The dashboard list endpoint excludes project_data
                              // for payload size, so we can't compute hasImages
                              // reliably here. Treat any completed Adstudio project
                              // as "has images" — handleViewAdstudioImages will
                              // fetch the full record and surface them.
                              const isCompleted = project.status === 'completed';

                              if (isCompleted) {
                                return (
                                  <div className="space-y-2">
                                    <Button
                                      onClick={() => handleViewAdstudioImages(project)}
                                      className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                                      data-testid={`view-images-${project.id}`}
                                    >
                                      <ImageIcon className="w-4 h-4 mr-2" />
                                      View Generated Images
                                    </Button>
                                    <Button
                                      onClick={() => handleContinueAdstudioProject(project.id)}
                                      variant="outline"
                                      className="w-full border-teal-300 text-teal-600 hover:bg-teal-50"
                                      data-testid={`edit-project-${project.id}`}
                                    >
                                      <Eye className="w-4 h-4 mr-2" />
                                      Edit Project
                                    </Button>
                                  </div>
                                );
                              }

                              // Otherwise show continue button
                              return (
                                <Button
                                  onClick={() => handleContinueAdstudioProject(project.id)}
                                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
                                  data-testid={`continue-project-${project.id}`}
                                >
                                  <Play className="w-4 h-4 mr-2" />
                                  Continue with Project
                                </Button>
                              );
                            })()}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Movie Maker Tab */}
          {activeTab === "movie-maker" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Movie Maker</h1>
                  <p className="text-gray-600">Create short movies with AI storytelling</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setSeriesDialog({ open: true, mode: 'create', series: null })}
                    className="border-orange-300 text-orange-600 hover:bg-orange-50"
                    data-testid="create-series-btn"
                  >
                    <FolderOpen className="w-4 h-4 mr-2" />
                    New Series
                  </Button>
                  <Button
                    onClick={() => launchApp("movie-maker", process.env.REACT_APP_MOVIE_MAKER_URL || 'https://moviestudio.loopai.in')}
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                    data-testid="create-movie-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Movie
                  </Button>
                </div>
              </div>

              {movieMakerProjects.length === 0 ? (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-8">
                    <div className="text-center py-12">
                      <Clapperboard className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No movie projects yet</h3>
                      <p className="text-gray-500 mb-6">Create your first AI-powered movie</p>
                      <Button
                        onClick={() => launchApp("movie-maker", process.env.REACT_APP_MOVIE_MAKER_URL || 'https://moviestudio.loopai.in')}
                        className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Your First Movie
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Series Folders */}
                  {seriesList.map(series => {
                    const isExpanded = expandedSeries[series.id] !== false;
                    const seriesProjectIds = (series.episodes || []).map(ep => ep.project_id);
                    const seriesProjects = movieMakerProjects.filter(p => seriesProjectIds.includes(p.id));
                    
                    return (
                      <Card key={series.id} className="border-0 shadow-md overflow-hidden" data-testid={`series-folder-${series.id}`}>
                        {/* Series Header */}
                        <div
                          className="flex items-center gap-3 px-6 py-4 cursor-pointer bg-gradient-to-r from-orange-50 to-amber-50 hover:from-orange-100 hover:to-amber-100 transition-colors"
                          onClick={() => setExpandedSeries(prev => ({ ...prev, [series.id]: !isExpanded }))}
                        >
                          <FolderOpen className="w-5 h-5 text-orange-500" />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-800 truncate">{series.name}</h3>
                            {series.description && (
                              <p className="text-xs text-gray-500 truncate">{series.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">
                              {seriesProjects.length} episode{seriesProjects.length !== 1 ? 's' : ''}
                            </span>
                            {series.genre && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{series.genre}</span>
                            )}
                            <Button
                              variant="ghost" size="sm"
                              onClick={(e) => { e.stopPropagation(); setSeriesDialog({ open: true, mode: 'edit', series }); }}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <Wand2 className="w-4 h-4" />
                            </Button>
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                          </div>
                        </div>
                        
                        {/* Series Episodes */}
                        {isExpanded && (
                          <CardContent className="p-6 pt-4">
                            {seriesProjects.length === 0 ? (
                              <div className="text-center py-6 text-gray-400">
                                <Film className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No episodes yet. Add movies from Standalone Projects below.</p>
                              </div>
                            ) : (
                              <>
                                {seriesProjects.length > 1 && (
                                  <p
                                    className="text-xs text-gray-400 mb-3 italic"
                                    data-testid={`series-reorder-hint-${series.id}`}
                                  >
                                    Tip: drag any card by its handle to rearrange episodes.
                                  </p>
                                )}
                                <SeriesEpisodesList
                                  series={series}
                                  projectsById={new Map(movieMakerProjects.map((p) => [p.id, p]))}
                                  renderCard={renderMovieCard}
                                  onReorder={async (orderedIds) => {
                                    // Optimistic update
                                    setSeriesList((prev) =>
                                      prev.map((s) => {
                                        if (s.id !== series.id) return s;
                                        const byId = Object.fromEntries((s.episodes || []).map((ep) => [ep.project_id, ep]));
                                        const nextEpisodes = orderedIds
                                          .map((pid, i) => (byId[pid] ? { ...byId[pid], episode_number: i + 1 } : null))
                                          .filter(Boolean);
                                        return { ...s, episodes: nextEpisodes, manual_order: true };
                                      })
                                    );
                                    try {
                                      await axios.put(
                                        `${BACKEND_URL}/api/series/${series.id}/episodes/reorder`,
                                        { episode_order: orderedIds },
                                        { withCredentials: true }
                                      );
                                    } catch (err) {
                                      toast.error("Failed to save new order");
                                      // Reload from server to recover
                                      try {
                                        const seriesRes = await axios.get(`${BACKEND_URL}/api/series`, { withCredentials: true });
                                        setSeriesList(seriesRes.data || []);
                                      } catch (_) {}
                                    }
                                  }}
                                />
                              </>
                            )}
                          </CardContent>
                        )}
                      </Card>
                    );
                  })}

                  {/* Standalone Projects (not in any series) */}
                  {(() => {
                    const seriesProjectIds = new Set(seriesList.flatMap(s => (s.episodes || []).map(ep => ep.project_id)));
                    const standalone = movieMakerProjects.filter(p => !seriesProjectIds.has(p.id));
                    
                    if (standalone.length === 0) return null;
                    
                    return (
                      <Card className="border-0 shadow-md" data-testid="standalone-projects">
                        <div className="px-6 py-4 bg-gray-50 border-b">
                          <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                            <Film className="w-5 h-5 text-gray-400" />
                            Standalone Projects
                          </h3>
                          <p className="text-xs text-gray-500">Not part of any series — drag into a folder to organize</p>
                        </div>
                        <CardContent className="p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {standalone.map(project => renderMovieCard(project, null, null))}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}
                </div>
              )}

              {/* Create/Edit Series Dialog */}
              <Dialog open={seriesDialog.open} onOpenChange={(open) => !open && setSeriesDialog({ open: false, mode: 'create', series: null })}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>{seriesDialog.mode === 'edit' ? 'Edit Series' : 'Create New Series'}</DialogTitle>
                    <DialogDescription>Organize your movie projects into a series with episodes.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const payload = {
                      name: formData.get('name'),
                      description: formData.get('description'),
                      genre: formData.get('genre'),
                      language: formData.get('language')
                    };
                    try {
                      if (seriesDialog.mode === 'edit') {
                        await axios.put(`${BACKEND_URL}/api/series/${seriesDialog.series.id}`, payload, { withCredentials: true });
                        toast.success("Series updated");
                      } else {
                        await axios.post(`${BACKEND_URL}/api/series`, payload, { withCredentials: true });
                        toast.success("Series created");
                      }
                      setSeriesDialog({ open: false, mode: 'create', series: null });
                      // Refresh series list only (lighter than full dashboard reload)
                      try {
                        const seriesRes = await axios.get(`${BACKEND_URL}/api/series`, { withCredentials: true });
                        setSeriesList(seriesRes.data || []);
                      } catch (e) { /* ignore refresh error */ }
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Failed to save series");
                    }
                  }}>
                    <div className="space-y-3 py-4">
                      <input name="name" placeholder="Series Name" required defaultValue={seriesDialog.series?.name || ''}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none" data-testid="series-name-input" />
                      <input name="description" placeholder="Description (optional)" defaultValue={seriesDialog.series?.description || ''}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none" />
                      <div className="grid grid-cols-2 gap-3">
                        <input name="genre" placeholder="Genre" defaultValue={seriesDialog.series?.genre || ''}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none" />
                        <input name="language" placeholder="Language" defaultValue={seriesDialog.series?.language || ''}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none" />
                      </div>
                    </div>
                    <div className="flex justify-between">
                      {seriesDialog.mode === 'edit' && (
                        <Button type="button" variant="ghost" className="text-red-500 hover:text-red-700" onClick={async () => {
                          if (window.confirm(`Delete series "${seriesDialog.series?.name}"? Episodes will become standalone.`)) {
                            try {
                              await axios.delete(`${BACKEND_URL}/api/series/${seriesDialog.series.id}`, { withCredentials: true });
                              toast.success("Series deleted");
                              setSeriesDialog({ open: false, mode: 'create', series: null });
                              try {
                                const seriesRes = await axios.get(`${BACKEND_URL}/api/series`, { withCredentials: true });
                                setSeriesList(seriesRes.data || []);
                              } catch (e) { /* ignore */ }
                            } catch (err) { toast.error("Failed to delete"); }
                          }
                        }}>
                          <Trash2 className="w-4 h-4 mr-1" /> Delete Series
                        </Button>
                      )}
                      <div className="flex gap-2 ml-auto">
                        <Button type="button" variant="outline" onClick={() => setSeriesDialog({ open: false, mode: 'create', series: null })}>Cancel</Button>
                        <Button type="submit" className="bg-gradient-to-r from-orange-500 to-red-500" data-testid="save-series-btn">
                          {seriesDialog.mode === 'edit' ? 'Save Changes' : 'Create Series'}
                        </Button>
                      </div>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Add to Series Dialog */}
              <Dialog open={addToSeriesDialog.open} onOpenChange={(open) => !open && setAddToSeriesDialog({ open: false, projectId: null })}>
                <DialogContent className="sm:max-w-sm">
                  <DialogHeader>
                    <DialogTitle>Add to Series</DialogTitle>
                    <DialogDescription>Choose a series to add this project to.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2 py-4 max-h-60 overflow-y-auto">
                    {seriesList.map(s => (
                      <button key={s.id} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-orange-50 transition-colors text-left border"
                        onClick={async () => {
                          try {
                            await axios.post(`${BACKEND_URL}/api/series/${s.id}/episodes`, { project_id: addToSeriesDialog.projectId }, { withCredentials: true });
                            toast.success(`Added to "${s.name}"`);
                            setAddToSeriesDialog({ open: false, projectId: null });
                            try {
                              const seriesRes = await axios.get(`${BACKEND_URL}/api/series`, { withCredentials: true });
                              setSeriesList(seriesRes.data || []);
                            } catch (e) { /* ignore */ }
                          } catch (err) { toast.error(err.response?.data?.detail || "Failed to add"); }
                        }}
                      >
                        <FolderOpen className="w-5 h-5 text-orange-500 shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{s.name}</p>
                          <p className="text-xs text-gray-500">{s.episode_count || 0} episodes</p>
                        </div>
                      </button>
                    ))}
                    {seriesList.length === 0 && (
                      <p className="text-center text-gray-500 text-sm py-4">No series yet. Create one first.</p>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === "payments" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Payment History</h1>
                  <p className="text-gray-600">Your transaction and subscription history</p>
                </div>
                <Button
                  onClick={() => navigate("/credits")}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Add Credits
                </Button>
              </div>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  {payments.length === 0 ? (
                    <div className="text-center py-12">
                      <Wallet className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No payment history</h3>
                      <p className="text-gray-500">Your transactions will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {payments.map(payment => (
                        <div key={payment.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{payment.description}</p>
                            <p className="text-sm text-gray-500">{new Date(payment.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">₹{payment.amount}</p>
                            <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">{payment.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* User Assets Tabs */}
          {activeTab.startsWith("assets-") && (
            <div className="space-y-6">
              {assetCategories.map((cat) => {
                if (activeTab !== `assets-${cat.id}`) return null;
                const assets = userAssets[cat.id] || [];
                const CatIcon = cat.icon;
                const isActors = cat.id === "actors";
                
                return (
                  <div key={cat.id}>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                          <CatIcon className="w-7 h-7 text-blue-500" />
                          {cat.label}
                        </h1>
                        <p className="text-gray-600">Manage your {cat.label.toLowerCase()} for use across all creator apps</p>
                      </div>
                      <Button
                        onClick={() => isActors ? setActorDialog(true) : setAssetUploadDialog({ open: true, category: cat.id })}
                        className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                        data-testid={`upload-${cat.id}-btn`}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {isActors ? "Add Actor" : `Upload ${cat.label.slice(0, -1)}`}
                      </Button>
                    </div>
                    
                    <Card className="border-0 shadow-lg">
                      <CardContent className="p-8">
                        {assetsLoading ? (
                          <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                          </div>
                        ) : assets.length === 0 ? (
                          <div className="text-center py-12">
                            <CatIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                            <h3 className="text-lg font-semibold text-gray-700 mb-2">No {cat.label.toLowerCase()} yet</h3>
                            <p className="text-gray-500 mb-6">{isActors ? "Add actors with photos, voice samples and metadata" : `Upload ${cat.label.toLowerCase()} to use them in your projects`}</p>
                            <Button
                              onClick={() => isActors ? setActorDialog(true) : setAssetUploadDialog({ open: true, category: cat.id })}
                              className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              {isActors ? "Add Your First Actor" : `Upload Your First ${cat.label.slice(0, -1)}`}
                            </Button>
                          </div>
                        ) : isActors ? (
                          /* Rich Actor Cards */
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                            {assets.map((actor) => (
                              <div key={actor.id} className="group rounded-xl border border-gray-200 hover:border-blue-400 bg-white shadow-sm hover:shadow-lg transition-all overflow-hidden" data-testid={`actor-card-${actor.id}`}>
                                {/* Thumbnail */}
                                <div className="relative aspect-[3/4] bg-gray-100">
                                  {actor.url ? (
                                    <img src={actor.url} alt={actor.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="flex items-center justify-center h-full"><Users className="w-12 h-12 text-gray-300" /></div>
                                  )}
                                  {/* Hover overlay */}
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                    {actor.url && <Button size="sm" variant="secondary" className="bg-white/90 hover:bg-white" onClick={() => openActorPreview(actor)} data-testid={`actor-view-btn-${actor.id}`}><Eye className="w-4 h-4" /></Button>}
                                    <Button size="sm" variant="secondary" className="bg-white/90 hover:bg-white" onClick={() => openEditActor(actor)} data-testid={`actor-edit-btn-${actor.id}`}><Pencil className="w-4 h-4" /></Button>
                                    <Button size="sm" variant="destructive" onClick={() => handleDeleteAsset(actor.id, actor.name)} data-testid={`actor-delete-btn-${actor.id}`}><Trash2 className="w-4 h-4" /></Button>
                                  </div>
                                  {/* Badges */}
                                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                                    {actor.actor_category && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-600 text-white">{actor.actor_category.replace("_", " ")}</span>}
                                    {actor.is_premium && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white">PREMIUM</span>}
                                  </div>
                                </div>
                                {/* Info */}
                                <div className="p-3 space-y-2">
                                  <p className="font-semibold text-gray-900 truncate">{actor.name}</p>
                                  <div className="flex flex-wrap gap-1 text-[10px]">
                                    {actor.gender && <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{actor.gender}</span>}
                                    {actor.age_group && <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{actor.age_group.replace("_", " ")}</span>}
                                    {actor.ethnicity && actor.ethnicity !== "OTHER" && <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{actor.ethnicity}</span>}
                                    {actor.style_type && <span className="px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600">{actor.style_type}</span>}
                                  </div>
                                  {actor.tags?.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {actor.tags.map((t, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600">{t}</span>)}
                                    </div>
                                  )}
                                  {/* Media indicators */}
                                  <div className="flex items-center gap-3 text-[11px] text-gray-400 pt-1 border-t border-gray-100">
                                    {actor.image_gallery?.length > 0 && <span className="flex items-center gap-1" title="Image gallery"><ImageIcon className="w-3 h-3" />{actor.image_gallery.length}</span>}
                                    {actor.preview_video_url && <span className="flex items-center gap-1" title="Preview video"><Video className="w-3 h-3" /></span>}
                                    {actor.voice_sample_url && <span className="flex items-center gap-1" title="Voice sample"><Play className="w-3 h-3" /></span>}
                                    {actor.voice_id && <span className="flex items-center gap-1 text-green-500" title="Voice cloned"><CheckCircle2 className="w-3 h-3" /></span>}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                            {assets.map((asset) => (
                              <div key={asset.id} className="group relative rounded-xl overflow-hidden border-2 border-gray-200 hover:border-blue-400 transition-all shadow-md hover:shadow-xl">
                                <img
                                  src={asset.url}
                                  alt={asset.name}
                                  className="w-full h-40 object-cover"
                                />
                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                  <Button size="sm" variant="secondary" onClick={() => window.open(asset.url, '_blank')} className="bg-white/90 hover:bg-white"><Eye className="w-4 h-4" /></Button>
                                  <Button size="sm" variant="destructive" onClick={() => handleDeleteAsset(asset.id, asset.name)}><Trash2 className="w-4 h-4" /></Button>
                                </div>
                                <div className="p-3 bg-white">
                                  <p className="font-medium text-gray-900 truncate">{asset.name}</p>
                                  {asset.description && <p className="text-xs text-gray-500 truncate">{asset.description}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Project Type Dialog */}
      <AlertDialog open={projectTypeDialog} onOpenChange={setProjectTypeDialog}>
        <AlertDialogContent className="max-w-5xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold text-center mb-2">Create New Project</AlertDialogTitle>
            <AlertDialogDescription className="text-center text-base">Select the type of content you want to create</AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 py-4">
            {/* Content Planner Card */}
            <div
              onClick={() => {
                setProjectTypeDialog(false);
                launchApp("content-planner", process.env.REACT_APP_CONTENT_PLANNER_URL || 'https://planner.loopai.in');
              }}
              className="cursor-pointer group"
              data-testid="project-picker-content-planner"
            >
              <Card className={`border-2 ${hasToolAccess('content_planner') ? 'border-gray-200 hover:border-blue-500' : 'border-gray-200 opacity-75'} hover:shadow-lg transition-all duration-200 h-full`}>
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <Calendar className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-base">Content Planner</CardTitle>
                </CardHeader>
                <CardContent className="text-center px-3 pb-4">
                  <p className="text-xs text-gray-600 mb-3">
                    Plan weekly &amp; monthly content for your business or brand. Build calendars, briefs, and post ideas in one place.
                  </p>
                  {hasToolAccess('content_planner') ? (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Active</span>
                  ) : (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium inline-flex items-center justify-center gap-1">
                      <Crown className="w-3 h-3" /> Creator+
                    </span>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Adstudio Card */}
            <div
              onClick={() => {
                setProjectTypeDialog(false);
                launchApp("adstudio", process.env.REACT_APP_ADSTUDIO_URL || 'https://adstudio.loopai.in');
              }}
              className="cursor-pointer group"
              data-testid="project-picker-adstudio"
            >
              <Card className={`border-2 ${hasToolAccess('adstudio') ? 'border-gray-200 hover:border-teal-500' : 'border-gray-200 opacity-75'} hover:shadow-lg transition-all duration-200 h-full`}>
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <BookImage className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-base">Adstudio</CardTitle>
                </CardHeader>
                <CardContent className="text-center px-3 pb-4">
                  <p className="text-xs text-gray-600 mb-3">
                    Generate high quality images for product catalogs, ads, social media & brand identity
                  </p>
                  {hasToolAccess('adstudio') ? (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Active</span>
                  ) : (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium inline-flex items-center justify-center gap-1">
                      <Crown className="w-3 h-3" /> Upgrade
                    </span>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Ad Creator Card */}
            <div
              onClick={() => {
                setProjectTypeDialog(false);
                launchApp("ad-creator", process.env.REACT_APP_AD_CREATOR_URL || 'https://ads.loopai.in');
              }}
              className="cursor-pointer group"
              data-testid="project-picker-ad-creator"
            >
              <Card className={`border-2 ${hasToolAccess('ad_creator') ? 'border-gray-200 hover:border-purple-500' : 'border-gray-200 opacity-75'} hover:shadow-lg transition-all duration-200 h-full`}>
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <Video className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-base">Ad Creator</CardTitle>
                </CardHeader>
                <CardContent className="text-center px-3 pb-4">
                  <p className="text-xs text-gray-600 mb-3">
                    Create engaging promotional and advertisement videos with AI-powered automation
                  </p>
                  {hasToolAccess('ad_creator') ? (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Active</span>
                  ) : (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium inline-flex items-center justify-center gap-1">
                      <Crown className="w-3 h-3" /> Starter+
                    </span>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Advanced Studio Card */}
            <div
              onClick={() => {
                if (!hasToolAccess('advanced_studio')) {
                  setProjectTypeDialog(false);
                  launchApp("studio-pro", process.env.REACT_APP_VIDEO_CREATOR_URL || 'https://studio.loopai.in');
                } else {
                  checkLowCreditsAndProceed(() => {
                    setProjectTypeDialog(false);
                    handleOpenProject(process.env.REACT_APP_VIDEO_CREATOR_URL || 'https://studio.loopai.in');
                  }, "studio-pro");
                }
              }}
              className="cursor-pointer group"
              data-testid="project-picker-studio-pro"
            >
              <Card className={`border-2 ${hasToolAccess('advanced_studio') ? 'border-gray-200 hover:border-pink-500' : 'border-gray-200 opacity-75'} hover:shadow-lg transition-all duration-200 h-full`}>
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto w-14 h-14 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <Film className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-base">Studio Pro</CardTitle>
                </CardHeader>
                <CardContent className="text-center px-3 pb-4">
                  <p className="text-xs text-gray-600 mb-3">
                    Complete creative control to edit each shot of your video with precision
                  </p>
                  {hasToolAccess('advanced_studio') ? (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Active</span>
                  ) : (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium flex items-center justify-center gap-1">
                      <Crown className="w-3 h-3" /> Creator+
                    </span>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Movie Maker Card */}
            <div
              onClick={() => {
                if (!hasToolAccess('movie_maker')) {
                  setProjectTypeDialog(false);
                  launchApp("movie-maker", process.env.REACT_APP_MOVIE_MAKER_URL || 'https://moviestudio.loopai.in');
                } else {
                  checkLowCreditsAndProceed(() => {
                    setProjectTypeDialog(false);
                    handleOpenProject(process.env.REACT_APP_MOVIE_MAKER_URL || 'https://moviestudio.loopai.in');
                  }, "movie-maker");
                }
              }}
              className="cursor-pointer group"
              data-testid="project-picker-movie-maker"
            >
              <Card className={`border-2 ${hasToolAccess('movie_maker') ? 'border-gray-200 hover:border-orange-500' : 'border-gray-200 opacity-75'} hover:shadow-lg transition-all duration-200 h-full`}>
                <CardHeader className="text-center pb-2">
                  <div className="mx-auto w-14 h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <Clapperboard className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-base">Movie Maker</CardTitle>
                </CardHeader>
                <CardContent className="text-center px-3 pb-4">
                  <p className="text-xs text-gray-600 mb-3">
                    Generate short movies and videos with AI-powered storytelling capabilities
                  </p>
                  {hasToolAccess('movie_maker') ? (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Active</span>
                  ) : (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium flex items-center justify-center gap-1">
                      <Crown className="w-3 h-3" /> Studio Only
                    </span>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The project and all associated data will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleDeleteProject(deleteDialog.id, deleteDialog.type);
                setDeleteDialog({ open: false, type: null, id: null });
              }}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Plans Dialog */}
      <AlertDialog open={plansDialog} onOpenChange={(open) => {
        if (!open && forcePlanSelection) {
          toast.info("Please select a plan to continue");
          return;
        }
        setPlansDialog(open);
      }}>
        <AlertDialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-3xl font-bold text-center mb-2">
              {forcePlanSelection ? "Welcome! Choose Your Plan" : "Choose Your Plan"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-base">
              {forcePlanSelection ? (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 font-semibold text-lg mb-1">🎉 Welcome to LoopAI Creator!</p>
                  <p className="text-gray-600">Select a plan to get started. You can always upgrade later.</p>
                </div>
              ) : (
                subscription?.plan_type !== "pay_as_you_go" && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-blue-800 font-semibold">
                      Current Plan: {subscription?.plan_name}
                      {subscription?.expires_at && (
                        <span className="ml-2 text-sm font-normal">(Expires: {new Date(subscription.expires_at).toLocaleDateString()})</span>
                      )}
                    </p>
                  </div>
                )
              )}
              Select the plan that best fits your needs
            </AlertDialogDescription>
          </AlertDialogHeader>

          {plans && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 py-4">
              {Object.entries(plans).map(([planType, plan]) => {
                const isActive = subscription?.plan_type === planType && !forcePlanSelection;
                return (
                  <Card key={planType} className={`relative border-2 transition-all ${isActive ? "border-blue-500 shadow-lg" : "border-gray-200 hover:border-blue-300"}`}>
                    {isActive && (
                      <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs px-2 py-1 rounded-bl-lg rounded-tr-lg font-semibold">ACTIVE</div>
                    )}
                    <CardHeader className="text-center pb-3">
                      <div className="mx-auto mb-3">{getPlanIcon(planType)}</div>
                      <CardTitle className="text-lg mb-2">{plan.name}</CardTitle>
                      <div className="text-2xl font-bold text-gray-900">
                        {plan.price === 0 ? "Free" : (<>₹{plan.price.toLocaleString()}{plan.billing_cycle && <span className="text-sm font-normal text-gray-500">/mo</span>}</>)}
                      </div>
                      {plan.credits > 0 && <p className="text-xs text-gray-500 mt-1">{plan.credits.toLocaleString()} credits</p>}
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ul className="space-y-2 mb-4">
                        {plan.features.slice(0, 4).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-xs">
                            <Check className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        onClick={() => {
                          if (forcePlanSelection && planType === "pay_as_you_go") {
                            handleSelectInitialPlan(planType);
                          } else {
                            handlePurchasePlan(planType);
                          }
                        }}
                        disabled={isActive || purchasing === planType}
                        className={`w-full text-sm ${
                          planType === "creator_pack" ? "bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                          : planType === "studio_pack" ? "bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                          : "bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                        }`}
                      >
                        {purchasing === planType ? "Processing..." : isActive ? "Current Plan" : forcePlanSelection ? (planType === "pay_as_you_go" ? "Get Started Free" : "Select Plan") : planType === "pay_as_you_go" ? "Free Plan" : "Purchase Plan"}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <AlertDialogFooter>
            {!forcePlanSelection && <AlertDialogCancel>Close</AlertDialogCancel>}
            {forcePlanSelection && <p className="text-sm text-gray-500 italic">Please select a plan to continue</p>}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image Creator Project View Modal */}
      <Dialog open={imageCreatorModal.open} onOpenChange={(open) => setImageCreatorModal({ ...imageCreatorModal, open })}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {imageCreatorModal.project?.name || 'Project Images'}
            </DialogTitle>
            <DialogDescription>
              {imageCreatorModal.project?.project_data?.concept || 'View and manage your generated images'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {loadingProjectImages ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            ) : imageCreatorModal.images.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No images found in this project</p>
              </div>
            ) : (
              <>
                <div className="flex justify-end mb-4">
                  <Button
                    onClick={() => handleDownloadAllImages(imageCreatorModal.images, imageCreatorModal.project?.name)}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download All ({imageCreatorModal.images.length})
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {imageCreatorModal.images.map((image, index) => {
                    const imageUrl = image.url || image.image_url || image;
                    return (
                      <div key={index} className="relative group rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 transition-all">
                        <img 
                          src={imageUrl} 
                          alt={`Generated image ${index + 1}`}
                          className="w-full h-48 object-cover"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleDownloadImage(imageUrl, `${imageCreatorModal.project?.name || 'image'}_${index + 1}.png`)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleShareImage(imageUrl)}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                        {image.prompt && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 truncate">
                            {image.prompt}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Adstudio Images Modal */}
      <Dialog open={adstudioImagesModal.open} onOpenChange={(open) => setAdstudioImagesModal({ ...adstudioImagesModal, open })}>
        <DialogContent className="max-w-[95vw] w-[1400px] max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <BookImage className="w-7 h-7 text-teal-500" />
              {adstudioImagesModal.project?.name || 'Adstudio Project Images'}
            </DialogTitle>
            <DialogDescription className="text-base">
              View and download your generated ad images
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            {loadingProjectImages ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              </div>
            ) : adstudioImagesModal.images.length === 0 ? (
              <div className="text-center py-16">
                <BookImage className="w-20 h-20 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg">No images found in this project</p>
                <p className="text-sm text-gray-400 mt-2">Generate images in Adstudio to see them here</p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-6">
                  <p className="text-gray-600">{adstudioImagesModal.images.length} image(s) generated</p>
                  <Button
                    onClick={() => handleDownloadAllImages(adstudioImagesModal.images, adstudioImagesModal.project?.name)}
                    className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download All ({adstudioImagesModal.images.length})
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {adstudioImagesModal.images.map((image, index) => {
                    const imageUrl = image.url || image.image_url || image;
                    return (
                      <div key={index} className="relative group rounded-xl overflow-hidden border-2 border-gray-200 hover:border-teal-400 transition-all shadow-lg hover:shadow-xl">
                        <img 
                          src={imageUrl} 
                          alt={`Generated ad image ${index + 1}`}
                          className="w-full h-72 object-cover"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                          <Button
                            size="lg"
                            variant="secondary"
                            onClick={() => handleDownloadImage(imageUrl, `${adstudioImagesModal.project?.name || 'adstudio'}_${index + 1}.png`)}
                            className="bg-white/90 hover:bg-white"
                          >
                            <Download className="w-5 h-5 mr-2" />
                            Download
                          </Button>
                          <Button
                            size="lg"
                            variant="secondary"
                            onClick={() => handleShareImage(imageUrl)}
                            className="bg-white/90 hover:bg-white"
                          >
                            <ExternalLink className="w-5 h-5 mr-2" />
                            Open
                          </Button>
                        </div>
                        {(image.prompt || image.concept) && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent text-white text-sm p-4">
                            {image.concept || image.prompt}
                          </div>
                        )}
                        <div className="absolute top-3 left-3 bg-teal-500 text-white text-xs font-bold px-2 py-1 rounded">
                          #{index + 1}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Player Modal */}
      <Dialog open={videoPlayerModal.open} onOpenChange={(open) => setVideoPlayerModal({ ...videoPlayerModal, open })}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{videoPlayerModal.projectName}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            {videoPlayerModal.videoUrl && (
              <video src={videoPlayerModal.videoUrl} controls className="w-full h-full" autoPlay />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tutorial Video Player Modal */}
      <Dialog open={tutorialVideoModal.open} onOpenChange={(open) => { if (!open) setTutorialVideoModal({ open: false, url: null, title: null, type: null }); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{tutorialVideoModal.title}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            {tutorialVideoModal.url && tutorialVideoModal.type === 'youtube' ? (
              <iframe
                src={tutorialVideoModal.url + "?autoplay=1"}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={tutorialVideoModal.title}
              />
            ) : tutorialVideoModal.url ? (
              <video src={tutorialVideoModal.url} controls autoPlay className="w-full h-full" />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Example Project Viewer Modal */}
      <Dialog open={exampleProjectModal.open} onOpenChange={(open) => { if (!open) setExampleProjectModal({ open: false, project: null }); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {exampleProjectModal.project?.title}
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                {exampleProjectModal.project?.type}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="rounded-lg overflow-hidden bg-gray-100">
            {exampleProjectModal.project?.type === 'Video' && exampleProjectModal.project?.media_url ? (
              <video
                src={exampleProjectModal.project.media_url}
                controls
                autoPlay
                className="w-full max-h-[70vh] object-contain bg-black"
              />
            ) : exampleProjectModal.project?.media_url ? (
              <img
                src={exampleProjectModal.project.media_url}
                alt={exampleProjectModal.project?.title}
                className="w-full max-h-[70vh] object-contain"
              />
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                No media available
              </div>
            )}
          </div>
          {exampleProjectModal.project?.description && (
            <p className="text-sm text-gray-600 mt-2">{exampleProjectModal.project.description}</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Actor Dialog */}
      <VoiceLibraryModal
        open={voiceLibraryOpen}
        onClose={() => setVoiceLibraryOpen(false)}
        onPick={(vid) => setActorVoiceId(vid)}
      />
      <Dialog open={actorDialog} onOpenChange={setActorDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="actor-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Add New Actor
            </DialogTitle>
            <DialogDescription>Create an actor with metadata, reference images and voice sample</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleActorUpload} className="space-y-4">
            {/* Name */}
            <div>
              <label className="text-sm font-medium text-gray-700">Name *</label>
              <input name="actorName" required placeholder="Actor name" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="actor-name-input" />
            </div>

            {/* Row: Category + Gender */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Category</label>
                <select name="actorCategory" defaultValue="REAL_HUMAN" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="actor-category-select">
                  <option value="REAL_HUMAN">Real Human</option>
                  <option value="AI_GENERATED">AI Generated</option>
                  <option value="CELEBRITY">Celebrity</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Gender</label>
                <select name="gender" defaultValue="MALE" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="actor-gender-select">
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                </select>
              </div>
            </div>

            {/* Row: Age Group + Ethnicity */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Age Group</label>
                <select name="ageGroup" defaultValue="ADULT" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="actor-age-select">
                  <option value="TEEN">Teen</option>
                  <option value="YOUNG_ADULT">Young Adult</option>
                  <option value="ADULT">Adult</option>
                  <option value="SENIOR">Senior</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Ethnicity</label>
                <select name="ethnicity" defaultValue="OTHER" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="actor-ethnicity-select">
                  <option value="INDIAN">Indian</option>
                  <option value="CAUCASIAN">Caucasian</option>
                  <option value="AFRICAN">African</option>
                  <option value="EAST_ASIAN">East Asian</option>
                  <option value="SOUTHEAST_ASIAN">Southeast Asian</option>
                  <option value="LATINO">Latino</option>
                  <option value="MIDDLE_EASTERN">Middle Eastern</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>

            {/* Row: Style Type + Credits */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Style Type</label>
                <select name="styleType" defaultValue="realistic" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="actor-style-select">
                  <option value="realistic">Realistic</option>
                  <option value="cinematic">Cinematic</option>
                  <option value="cartoon">Cartoon</option>
                  <option value="anime">Anime</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Credits (on selection)</label>
                <input name="actorCredits" type="number" min="0" defaultValue="0" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="actor-credits-input" />
              </div>
            </div>

            {/* Tags + Premium */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-700">Tags (comma separated)</label>
                <input name="tags" placeholder="corporate, fitness, luxury" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="actor-tags-input" />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input name="isPremium" type="checkbox" className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-testid="actor-premium-check" />
                  <span className="text-sm font-medium text-gray-700">Premium</span>
                </label>
              </div>
            </div>

            {/* Thumbnail */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Thumbnail Image</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                <input type="file" name="thumbnail" accept="image/*" className="hidden" id="actor-thumb-input" data-testid="actor-thumbnail-input"
                  onChange={(e) => { const el = document.getElementById('actor-thumb-label'); if (el && e.target.files?.[0]) { el.textContent = e.target.files[0].name; el.className = 'text-sm text-blue-600 font-medium'; }}} />
                <label htmlFor="actor-thumb-input" className="cursor-pointer">
                  <ImageIcon className="w-8 h-8 mx-auto text-gray-400 mb-1" />
                  <p id="actor-thumb-label" className="text-sm text-gray-500">Click to select thumbnail</p>
                  <p className="text-xs text-gray-400">JPEG, PNG, WebP (max 10MB)</p>
                </label>
              </div>
            </div>

            {/* Image Gallery */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Image Gallery (multiple)</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                <input type="file" name="galleryFiles" accept="image/*" multiple className="hidden" id="actor-gallery-input" data-testid="actor-gallery-input"
                  onChange={(e) => { const el = document.getElementById('actor-gallery-label'); if (el && e.target.files?.length) { el.textContent = `${e.target.files.length} image(s) selected`; el.className = 'text-sm text-blue-600 font-medium'; }}} />
                <label htmlFor="actor-gallery-input" className="cursor-pointer">
                  <ImageIcon className="w-8 h-8 mx-auto text-gray-400 mb-1" />
                  <p id="actor-gallery-label" className="text-sm text-gray-500">Click to select reference images</p>
                  <p className="text-xs text-gray-400">Select multiple images for AI consistency</p>
                </label>
              </div>
            </div>

            {/* Voice Sample — record / upload / preview / clone */}
            <VoiceSampleControls
              onSampleChange={(file) => setActorVoiceFile(file)}
              onCloned={(vid) => setActorVoiceId(vid)}
            />

            {/* Voice ID + Preview Video URL */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Voice ID (ElevenLabs)</label>
                <div className="flex gap-1 mt-1">
                  <input
                    name="voiceId"
                    placeholder="e.g. ztmflAeN..."
                    className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    value={actorVoiceId}
                    onChange={(e) => setActorVoiceId(e.target.value)}
                    data-testid="actor-voiceid-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="whitespace-nowrap"
                    onClick={() => setVoiceLibraryOpen(true)}
                    data-testid="actor-voiceid-browse-btn"
                  >
                    Browse
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Preview Video URL</label>
                <input name="previewVideoUrl" placeholder="https://..." className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-testid="actor-video-url-input" />
              </div>
            </div>

            {/* Default Prompt */}
            <div>
              <label className="text-sm font-medium text-gray-700">Default Prompt</label>
              <textarea name="defaultPrompt" rows={2} placeholder="AI prompt for visual consistency..." className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm" data-testid="actor-prompt-input" />
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium text-gray-700">Description</label>
              <textarea name="description" rows={2} placeholder="Brief description..." className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm" />
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setActorDialog(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600" disabled={uploadingAsset} data-testid="actor-submit-btn">
                {uploadingAsset ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Creating...</> : <><Plus className="w-4 h-4 mr-2" />Create Actor</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Actor Dialog (full edit) */}
      <VoiceLibraryModal
        open={editVoiceLibraryFullOpen}
        onClose={() => setEditVoiceLibraryFullOpen(false)}
        onPick={(vid) => setEditVoiceIdFull(vid)}
      />
      <Dialog open={!!editActor} onOpenChange={(o) => { if (!o) setEditActor(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="actor-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-500" />
              Edit Actor
            </DialogTitle>
            <DialogDescription>Update actor metadata, images, gallery, and voice</DialogDescription>
          </DialogHeader>
          {editActor && (
            <form onSubmit={handleUpdateActor} className="space-y-4" key={editActor.id}>
              {/* Name */}
              <div>
                <label className="text-sm font-medium text-gray-700">Name *</label>
                <input name="actorName" required defaultValue={editActor.name || ""} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-actor-name-input" />
              </div>

              {/* Category + Gender */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Category</label>
                  <select name="actorCategory" defaultValue={editActor.actor_category || "REAL_HUMAN"} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-actor-category-select">
                    <option value="REAL_HUMAN">Real Human</option>
                    <option value="AI_GENERATED">AI Generated</option>
                    <option value="CELEBRITY">Celebrity</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Gender</label>
                  <select name="gender" defaultValue={editActor.gender || "MALE"} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-actor-gender-select">
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                  </select>
                </div>
              </div>

              {/* Age + Ethnicity */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Age Group</label>
                  <select name="ageGroup" defaultValue={editActor.age_group || "ADULT"} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-actor-age-select">
                    <option value="TEEN">Teen</option>
                    <option value="YOUNG_ADULT">Young Adult</option>
                    <option value="ADULT">Adult</option>
                    <option value="SENIOR">Senior</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Ethnicity</label>
                  <select name="ethnicity" defaultValue={editActor.ethnicity || "OTHER"} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-actor-ethnicity-select">
                    <option value="INDIAN">Indian</option>
                    <option value="CAUCASIAN">Caucasian</option>
                    <option value="AFRICAN">African</option>
                    <option value="EAST_ASIAN">East Asian</option>
                    <option value="SOUTHEAST_ASIAN">Southeast Asian</option>
                    <option value="LATINO">Latino</option>
                    <option value="MIDDLE_EASTERN">Middle Eastern</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>

              {/* Style + Credits */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Style Type</label>
                  <select name="styleType" defaultValue={editActor.style_type || "realistic"} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-actor-style-select">
                    <option value="realistic">Realistic</option>
                    <option value="cinematic">Cinematic</option>
                    <option value="cartoon">Cartoon</option>
                    <option value="anime">Anime</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Credits (on selection)</label>
                  <input name="actorCredits" type="number" min="0" defaultValue={editActor.actor_credits ?? 0} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-actor-credits-input" />
                </div>
              </div>

              {/* Tags + Premium */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-700">Tags (comma separated)</label>
                  <input name="tags" defaultValue={(editActor.tags || []).join(", ")} placeholder="corporate, fitness, luxury" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="edit-actor-tags-input" />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input name="isPremium" type="checkbox" defaultChecked={!!editActor.is_premium} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-testid="edit-actor-premium-check" />
                    <span className="text-sm font-medium text-gray-700">Premium</span>
                  </label>
                </div>
              </div>

              {/* Current + New Thumbnail */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">Thumbnail Image</label>
                <div className="flex items-start gap-3">
                  {editActor.url && (
                    <img src={editActor.url} alt="current thumbnail" className="w-20 h-20 rounded-lg object-cover border border-gray-200" data-testid="edit-actor-current-thumb" />
                  )}
                  <div className="flex-1 border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-blue-400 transition-colors">
                    <input type="file" name="thumbnail" accept="image/*" className="hidden" id="edit-actor-thumb-input" data-testid="edit-actor-thumbnail-input"
                      onChange={(e) => { const el = document.getElementById('edit-actor-thumb-label'); if (el && e.target.files?.[0]) { el.textContent = e.target.files[0].name; el.className = 'text-sm text-blue-600 font-medium'; }}} />
                    <label htmlFor="edit-actor-thumb-input" className="cursor-pointer block">
                      <ImageIcon className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                      <p id="edit-actor-thumb-label" className="text-xs text-gray-500">Replace thumbnail (optional)</p>
                    </label>
                  </div>
                </div>
              </div>

              {/* Existing Gallery + Remove toggles */}
              {editActor.image_gallery?.length > 0 && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700">Current Gallery ({editActor.image_gallery.length})</label>
                  <div className="grid grid-cols-4 gap-2">
                    {editActor.image_gallery.map((url, i) => {
                      const marked = editRemoveGallery.includes(i);
                      return (
                        <button
                          type="button"
                          key={i}
                          onClick={() => toggleGalleryRemoval(i)}
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${marked ? 'border-red-500 opacity-40' : 'border-gray-200 hover:border-blue-400'}`}
                          data-testid={`edit-actor-gallery-toggle-${i}`}
                          title={marked ? "Click to keep" : "Click to remove on save"}
                        >
                          <img src={url} alt="" className="w-full h-full object-cover" />
                          {marked && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">REMOVE</div>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {editRemoveGallery.length > 0 && (
                    <p className="text-xs text-red-600">{editRemoveGallery.length} image(s) will be removed on save.</p>
                  )}
                </div>
              )}

              {/* Add new gallery */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">Add Gallery Images</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-blue-400 transition-colors">
                  <input type="file" name="galleryFiles" accept="image/*" multiple className="hidden" id="edit-actor-gallery-input" data-testid="edit-actor-gallery-input"
                    onChange={(e) => { const el = document.getElementById('edit-actor-gallery-label'); if (el && e.target.files?.length) { el.textContent = `${e.target.files.length} new image(s) selected`; el.className = 'text-sm text-blue-600 font-medium'; }}} />
                  <label htmlFor="edit-actor-gallery-input" className="cursor-pointer">
                    <ImageIcon className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                    <p id="edit-actor-gallery-label" className="text-xs text-gray-500">Add images (they append to gallery)</p>
                  </label>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-600 mt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editGalleryMode === "replace"}
                    onChange={(e) => setEditGalleryMode(e.target.checked ? "replace" : "append")}
                    className="w-3 h-3"
                    data-testid="edit-actor-gallery-replace-check"
                  />
                  Replace entire gallery with new uploads
                </label>
              </div>

              {/* Voice Sample */}
              {editActor.voice_sample_url && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Current Voice Sample</label>
                  <audio controls className="w-full h-9 mt-1" src={editActor.voice_sample_url} data-testid="edit-actor-current-voice-audio" />
                </div>
              )}
              <VoiceSampleControls
                onSampleChange={(file) => setEditVoiceFileFull(file)}
                onCloned={(vid) => setEditVoiceIdFull(vid)}
              />

              {/* Voice ID + Preview Video URL */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Voice ID (ElevenLabs)</label>
                  <div className="flex gap-1 mt-1">
                    <input
                      type="text"
                      placeholder="e.g. ztmflAeN..."
                      className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      value={editVoiceIdFull}
                      onChange={(e) => setEditVoiceIdFull(e.target.value)}
                      data-testid="edit-actor-voiceid-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="whitespace-nowrap"
                      onClick={() => setEditVoiceLibraryFullOpen(true)}
                      data-testid="edit-actor-voiceid-browse-btn"
                    >
                      Browse
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Preview Video URL</label>
                  <input name="previewVideoUrl" defaultValue={editActor.preview_video_url || ""} placeholder="https://..." className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-testid="edit-actor-video-url-input" />
                </div>
              </div>

              {/* Default Prompt */}
              <div>
                <label className="text-sm font-medium text-gray-700">Default Prompt</label>
                <textarea name="defaultPrompt" rows={2} defaultValue={editActor.default_prompt || ""} placeholder="AI prompt for visual consistency..." className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm" data-testid="edit-actor-prompt-input" />
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <textarea name="description" rows={2} defaultValue={editActor.description || ""} placeholder="Brief description..." className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm" data-testid="edit-actor-description-input" />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setEditActor(null)} data-testid="edit-actor-cancel-btn">Cancel</Button>
                <Button type="submit" className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600" disabled={editSaving} data-testid="edit-actor-save-btn">
                  {editSaving ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Saving...</> : <><Check className="w-4 h-4 mr-2" />Save Changes</>}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Actor Preview Modal */}
      <VoiceLibraryModal
        open={editVoiceLibraryOpen}
        onClose={() => setEditVoiceLibraryOpen(false)}
        onPick={(vid) => setEditVoiceIdInput(vid)}
      />
      {actorPreview && (
        <Dialog open={!!actorPreview} onOpenChange={() => setActorPreview(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="actor-preview-modal">
            <DialogHeader>
              <DialogTitle>{actorPreview.name}</DialogTitle>
              <DialogDescription>
                {[actorPreview.actor_category?.replace("_", " "), actorPreview.gender, actorPreview.age_group?.replace("_", " "), actorPreview.style_type].filter(Boolean).join(" · ")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Thumbnail */}
              {actorPreview.url && <img src={actorPreview.url} alt={actorPreview.name} className="w-full max-h-64 object-contain rounded-lg bg-gray-50" />}
              {/* Gallery */}
              {actorPreview.image_gallery?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Image Gallery ({actorPreview.image_gallery.length})</p>
                  <div className="grid grid-cols-3 gap-2">
                    {actorPreview.image_gallery.map((url, i) => <img key={i} src={url} alt="" className="w-full aspect-square object-cover rounded-lg" />)}
                  </div>
                </div>
              )}
              {/* Preview Video */}
              {actorPreview.preview_video_url && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Preview Video</p>
                  <video controls className="w-full rounded-lg" src={actorPreview.preview_video_url} />
                </div>
              )}
              {/* Voice Sample — editable */}
              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                  <Mic className="w-4 h-4 text-purple-500" /> Voice Sample
                </p>

                {actorPreview.voice_sample_url && (
                  <div className="mb-3">
                    <audio controls className="w-full" src={actorPreview.voice_sample_url} data-testid="actor-preview-voice-audio" />
                    {actorPreview.voice_id && (
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-green-600">Voice Cloned — ID: {actorPreview.voice_id}</p>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-6 text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={handleDeleteActorVoice}
                          disabled={editVoiceSaving}
                          data-testid="actor-preview-voice-delete-btn"
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Delete cloned voice
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                <VoiceSampleControls
                  initialSampleUrl={null}
                  onSampleChange={(file) => setEditVoiceFile(file)}
                  onCloned={(vid) => setEditVoiceIdInput(vid)}
                />

                {/* Voice ID input + Browse Library */}
                <div className="mt-3">
                  <label className="text-sm font-medium text-gray-700">Voice ID (ElevenLabs)</label>
                  <div className="flex gap-1 mt-1">
                    <input
                      type="text"
                      placeholder="e.g. ztmflAeN..."
                      className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      value={editVoiceIdInput}
                      onChange={(e) => setEditVoiceIdInput(e.target.value)}
                      data-testid="actor-preview-voiceid-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="whitespace-nowrap"
                      onClick={() => setEditVoiceLibraryOpen(true)}
                      data-testid="actor-preview-voiceid-browse-btn"
                    >
                      Browse
                    </Button>
                  </div>
                </div>

                <div className="mt-3 flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveActorVoice}
                    disabled={editVoiceSaving}
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
                    data-testid="actor-preview-voice-save-btn"
                  >
                    {editVoiceSaving ? "Saving…" : "Save Voice Changes"}
                  </Button>
                </div>
              </div>
              {/* Tags */}
              {actorPreview.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {actorPreview.tags.map((t, i) => <span key={i} className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-600">{t}</span>)}
                </div>
              )}
              {/* Default Prompt */}
              {actorPreview.default_prompt && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Default Prompt</p>
                  <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{actorPreview.default_prompt}</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Asset Upload Dialog */}
      <Dialog open={assetUploadDialog.open} onOpenChange={(open) => setAssetUploadDialog({ ...assetUploadDialog, open })}>
        <DialogContent className="max-w-md" data-testid="asset-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-500" />
              Upload {assetUploadDialog.category ? assetCategories.find(c => c.id === assetUploadDialog.category)?.label.slice(0, -1) : 'Asset'}
            </DialogTitle>
            <DialogDescription>
              Add a new {assetUploadDialog.category ? assetUploadDialog.category.slice(0, -1) : 'asset'} to your library
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssetUpload} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Name *</label>
              <input
                type="text"
                name="name"
                required
                placeholder={`Enter ${assetUploadDialog.category ? assetUploadDialog.category.slice(0, -1) : 'asset'} name`}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="asset-name-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Description (optional)</label>
              <textarea
                name="description"
                placeholder="Add a description..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                data-testid="asset-description-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Image *</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  name="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  required
                  className="hidden"
                  id="asset-file-input"
                  data-testid="asset-file-input"
                  onChange={(e) => {
                    const fileName = e.target.files?.[0]?.name;
                    const label = document.getElementById('asset-file-label');
                    if (label && fileName) {
                      label.textContent = fileName;
                      label.classList.remove('text-gray-500');
                      label.classList.add('text-blue-600', 'font-medium');
                    }
                  }}
                />
                <label htmlFor="asset-file-input" className="cursor-pointer">
                  <ImageIcon className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                  <p id="asset-file-label" className="text-sm text-gray-500">Click to select an image</p>
                  <p className="text-xs text-gray-400 mt-1">JPEG, PNG, WebP, GIF (max 10MB)</p>
                </label>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setAssetUploadDialog({ open: false, category: null })}
                data-testid="asset-upload-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                disabled={uploadingAsset}
                data-testid="asset-upload-submit"
              >
                {uploadingAsset ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
